// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage 6Q.1 Proforma frontend helpers (PURE).
//
// Mirror of the backend proformaCalculation engine so the editor grid can
// show live totals as the user types. The BACKEND remains authoritative —
// /preview and save recompute server-side. These helpers are pure ESM so
// they run under both Vite and the node:test runner.
//
// No hardcoded business data. Conversion factors + internal enums only.
// ════════════════════════════════════════════════════════════════════

import { isLamiOrDgu, assemblyTypeLabel } from './proformaNestingMasters.js';

export const MM_PER_INCH = 25.4;
export const MM_PER_CM = 10;
export const MM_PER_FT = 304.8;
export const MM_PER_M = 1000;
export const SQFT_PER_SQM = 10.764;

export const WASTAGE_MODES = ['ROUND_OFF', 'PLUS_MM', 'EVEN_IN', 'EVEN_CM', 'EVEN_FT'];
export const INPUT_UNITS = ['MM', 'IN', 'CM', 'FT'];
// Stage 6CM.3 — SQF/SQM are area-based (rate-pair); RFT/RMT/MTR are running
// length; PCS is per-piece. Mirrors backend PRICE_UOM.
export const PRICE_UOMS = ['SQF', 'SQM', 'RFT', 'RMT', 'MTR', 'PCS'];
export const AREA_MODES = ['SQFT', 'SQMT', 'RFT', 'RMT'];
// Stage SALES-PROFORMA-STATUS-1 — business lifecycle statuses.
export const STATUSES = ['PENDING', 'CONFIRMED', 'HOLD', 'CANCELLED'];

function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }

// ── Jumbo-size annotation (display only) ────────────────────────────────
// Mirror of the backend Other-Charges size gate (otherChargesCalculation.service
// `matchCondition`). Given the tenant's active "Jumbo" charge condition and a
// line's size, returns true when the line is "jumbo size" — used ONLY to show a
// "( JUMBO SIZE )" tag in the grid. It reads no rate/slab-value and moves NO
// money; the Jumbo charge itself is still computed 100% server-side. Judge on the
// CHARGE dimensions (falling back to actual) to match how the charge is billed.
// KEEP IN LOCKSTEP with the backend switch so grid and print agree.
export function matchJumboCondition(cond, line) {
  if (!cond || !line) return false;
  const ct = String(cond.conditionType || '').toUpperCase();
  const w = num(line.chargeWidthMm) || num(line.actualWidthMm);
  const h = num(line.chargeHeightMm) || num(line.actualHeightMm);
  // line.chargeSqm / actualSqm are the qty-SUMMED line totals (computeLeafRow
  // stores ×qty). The Jumbo condition judges ONE piece — a batch of small pieces
  // is not a large piece — so divide by qty to get per-piece area, exactly like
  // the backend `matchCondition` (perPieceArea). Dims (w/h) are already per-piece.
  const qty = num(line.qty) > 1 ? num(line.qty) : 1;
  const area = (num(line.chargeSqm) || num(line.actualSqm)) / qty;   // per-piece area (sqm)
  const limit = num(cond.dimensionLimitMm);
  const widthHit = limit > 0 && w > limit;
  const heightHit = limit > 0 && h > limit;
  const sizeHit = widthHit || heightHit;
  const areaLimit = num(cond.areaLimitSqm);
  const areaLimitHit = areaLimit > 0 && area > areaLimit;
  const slabHit = [cond.slab1Area, cond.slab2Area, cond.slab3Area].some((s) => num(s) > 0 && area >= num(s));
  switch (ct) {
    case 'ALWAYS':           return true;
    case 'WIDTH_EXCEEDS':    return widthHit;
    case 'HEIGHT_EXCEEDS':   return heightHit;
    case 'ANY_SIDE_EXCEEDS': return sizeHit;
    case 'AREA_EXCEEDS':     return areaLimitHit;
    case 'AREA_SLAB':        return slabHit;
    case 'SIZE_OR_AREA':     return sizeHit || areaLimitHit || slabHit;
    case 'SIZE_AND_AREA':    return sizeHit && (areaLimitHit || slabHit);
    default:                 return false;
  }
}

export function round1(n) {
  const v = num(n); const s = v < 0 ? -1 : 1;
  return s * Math.round((Math.abs(v) + Number.EPSILON) * 10) / 10;
}
export function round2(n) {
  const v = num(n); const s = v < 0 ? -1 : 1;
  return s * Math.round((Math.abs(v) + Number.EPSILON) * 100) / 100;
}
export function round3(n) {
  const v = num(n); const s = v < 0 ? -1 : 1;
  return s * Math.round((Math.abs(v) + Number.EPSILON) * 1e3) / 1e3;
}
export function round4(n) {
  const v = num(n); const s = v < 0 ? -1 : 1;
  return s * Math.round((Math.abs(v) + Number.EPSILON) * 1e4) / 1e4;
}

// ── dimensions ──────────────────────────────────────────────────────
export function normalizeDimensionByInputUnit(value, inputUnit) {
  const v = num(value);
  if (v <= 0) return 0;
  if (inputUnit === 'IN') return round4(v * MM_PER_INCH);
  if (inputUnit === 'CM') return round4(v * MM_PER_CM);
  if (inputUnit === 'FT') return round4(v * MM_PER_FT);
  return round1(v);   // MM — the MM column is kept to a single decimal place
}

export function applyRoundOff(actualMm, stepInches) {
  const a = num(actualMm); const step = num(stepInches);
  if (a <= 0 || step <= 0) return round4(a);
  const inches = a / MM_PER_INCH;
  const ratio = Math.round((inches / step) * 1e4) / 1e4;
  return round4(Math.ceil(ratio) * step * MM_PER_INCH);
}
export function applyPlusMm(actualMm, addMm) {
  const a = num(actualMm);
  return a <= 0 ? 0 : round4(a + Math.max(0, num(addMm)));
}
export function applyEvenIn(actualMm) {
  const a = num(actualMm);
  return a <= 0 ? 0 : round4(Math.ceil((a / MM_PER_INCH) / 2) * 2 * MM_PER_INCH);
}
export function applyEvenCm(actualMm) {
  const a = num(actualMm);
  return a <= 0 ? 0 : round4(Math.ceil((a / MM_PER_CM) / 2) * 2 * MM_PER_CM);
}
export function applyEvenFt(actualMm) {
  const a = num(actualMm);
  return a <= 0 ? 0 : round4(Math.ceil((a / MM_PER_FT) / 2) * 2 * MM_PER_FT);
}
export function calculateChargeDimension(actualMm, mode, step) {
  switch (mode) {
    case 'PLUS_MM': return applyPlusMm(actualMm, step);
    case 'EVEN_IN': return applyEvenIn(actualMm);
    case 'EVEN_CM': return applyEvenCm(actualMm);
    case 'EVEN_FT': return applyEvenFt(actualMm);
    case 'ROUND_OFF':
    default: return applyRoundOff(actualMm, step);
  }
}

// Stage 6CM.2 — Company Defaults chargeable-mode + round-off mirrors of the
// backend (proformaCalculation.service.js). MUST stay in lockstep. Absent flags
// reproduce the pre-6CM.2 behaviour (AUTO_FROM_WASTAGE + NEAREST round-off).
export function resolveChargeMm(actualMm, mode, editAllowed, manualMm, wMode, step) {
  const a = num(actualMm);
  const m = num(manualMm);
  const wastage = a > 0 ? calculateChargeDimension(a, wMode, step) : 0;
  switch (String(mode || 'AUTO_FROM_WASTAGE').toUpperCase()) {
    case 'LOCKED_TO_ACTUAL':    return a;
    case 'LOCKED_TO_WASTAGE':   return wastage;
    case 'MANUAL_EDIT_ALLOWED': return m > 0 ? m : wastage;
    // Wastage-on-entry (legacy, dormant): untouched → ACTUAL; a per-row override wins.
    case 'ACTUAL_STAMP_ON_ENTRY': return m > 0 ? m : a;
    // AUTO_FROM_WASTAGE (tenant default): charge = ACTUAL by default (Task-1: no
    // auto-wastage on every row). A per-row override wins — the editor stamps the
    // Wastage-Mode tolerance into it when the ACTUAL size is typed (Task-2), and a
    // manual CHRG edit sets it directly; honoured whether or not CHRG is editable.
    default:                    return m > 0 ? m : a;
  }
}

// Task-2 (2026-08-07) — the chargeable override that typing an ACTUAL size implies
// under the current Wastage Mode: the tolerance-adjusted size, or 0 when there is
// no tolerance (the row then falls back to ACTUAL). Pure; the grid stamps this into
// manualChargeWidthMm/HeightMm when the actual W/H is committed on a normal line.
export function chargeStampForActual(actualMm, wMode, step) {
  const a = num(actualMm);
  if (!(a > 0)) return 0;
  const st = calculateChargeDimension(a, wMode, step);
  return (st > 0 && st !== a) ? st : 0;
}

// Whether the CHRG W/H cells are user-editable under a given chargeable mode.
export function isChargeEditable(mode, editChargeable) {
  switch (String(mode || 'AUTO_FROM_WASTAGE').toUpperCase()) {
    case 'MANUAL_EDIT_ALLOWED': return true;
    case 'ACTUAL_STAMP_ON_ENTRY': return true;   // cells editable so entry can stamp + user can type
    case 'LOCKED_TO_ACTUAL':
    case 'LOCKED_TO_WASTAGE':   return false;
    default:                    return editChargeable === true; // AUTO_FROM_WASTAGE
  }
}

// Sheet cost mode (Stage 6Q.3) — how a SHEET part group earns its money:
//   • WASTAGE     → bill the leftover scrap area at the bottom of the group
//                   (per-material scrapRates). This is the default and the only
//                   behaviour any legacy document has ever had.
//   • SHEET_PRICE → price the sheet line itself (nestingConfig.parentRate × the
//                   sheet's own chargeable area × qty), like a normal line item;
//                   scrap costing is suppressed.
// The two are mutually exclusive. Anything unrecognised (or absent) is WASTAGE so
// existing proformas are untouched. MUST mirror the backend resolveSheetCostMode.
export const SHEET_COST_MODE = { WASTAGE: 'WASTAGE', SHEET_PRICE: 'SHEET_PRICE' };
export function resolveSheetCostMode(nestingConfig) {
  const raw = nestingConfig && nestingConfig.sheetCostMode;
  return String(raw || '').toUpperCase() === 'SHEET_PRICE' ? 'SHEET_PRICE' : 'WASTAGE';
}

// Document grand-total round-off (opts absent / enabled → NEAREST = prior).
export function applyDocRoundOff(preRound, opts) {
  const o = opts || {};
  if (o.roundOffEnabled === false) return { roundOffAmount: 0, grandTotal: round2(preRound) };
  let rounded;
  switch (String(o.roundOffMode || 'NEAREST').toUpperCase()) {
    case 'NONE': rounded = preRound; break;
    case 'UP':   rounded = Math.ceil(preRound); break;
    case 'DOWN': rounded = Math.floor(preRound); break;
    default:     rounded = Math.round(preRound);
  }
  return { roundOffAmount: round2(rounded - preRound), grandTotal: round2(rounded) };
}

// ── area ────────────────────────────────────────────────────────────
export function calculateAreaSqm(wMm, hMm) {
  const w = num(wMm); const h = num(hMm);
  if (w <= 0 || h <= 0) return 0;
  return round4((w / MM_PER_M) * (h / MM_PER_M));
}
export function calculateAreaSqft(sqm) { return round4(num(sqm) * SQFT_PER_SQM); }

// ── running length (perimeter) — used for edge-process (polish) pricing ──
// RFT/RMT are linear running measures of the piece edge (perimeter), the
// real basis for per-running-foot edge work. Both dims must be > 0.
export function calculatePerimeterRft(wMm, hMm) {
  const w = num(wMm); const h = num(hMm);
  if (w <= 0 || h <= 0) return 0;
  return round4((2 * (w + h)) / MM_PER_FT);
}
export function calculatePerimeterRmt(wMm, hMm) {
  const w = num(wMm); const h = num(hMm);
  if (w <= 0 || h <= 0) return 0;
  return round4((2 * (w + h)) / MM_PER_M);
}

// ── unit display (inverse of normalizeDimensionByInputUnit) ──
export const UNIT_FACTORS = { MM: 1, IN: MM_PER_INCH, CM: MM_PER_CM, FT: MM_PER_FT };
export const UNIT_PRECISION = { MM: 1, IN: 3, CM: 2, FT: 3 };
export function displayDimensionInUnit(mm, unit) {
  const v = num(mm);
  if (v <= 0) return null;
  const conv = v / (UNIT_FACTORS[unit] || 1);
  // MM shows at most ONE decimal place — a value converted into MM (unit change,
  // wastage / round-off charge size, imported dim) never carries sub-0.1mm noise
  // in the grid's MM column. IN/CM/FT keep their own display precision.
  return unit === 'MM' ? round1(conv) : round4(conv);
}

// ── item rate → document Price UOM ──────────────────────────────────
// An Item Master rate is stored in the item's own (default) UOM. When a
// product is picked, the proforma line must carry that rate in the
// DOCUMENT's Price UOM (from Proforma Settings) so calculateBasicAmount
// is correct. Area UOMs convert (SQF ⇄ SQM, 1 SQM = 10.764 SQF); any
// non-area item UOM (PCS / SET …) passes through unchanged with no alt.
const SQF_PER_SQM = 10.764; // GST + international standard.
function normAreaUom(c) {
  const u = String(c == null ? '' : c).trim().toUpperCase();
  if (u === 'SQFT') return 'SQF';
  if (u === 'SQMT') return 'SQM';
  return u;
}
// Convert a per-unit price from one area UOM to another. Returns null
// when `from` is not an area UOM (caller treats that as "no conversion").
function areaRate(rate, from, to) {
  if (from !== 'SQF' && from !== 'SQM') return null;
  if (from === to) return rate;
  // price per SQF → per SQM = × 10.764 ; per SQM → per SQF = ÷ 10.764
  return from === 'SQF' ? rate * SQF_PER_SQM : rate / SQF_PER_SQM;
}
// Stage 6CM.3 — true for the area-based pair (SQF/SQM/SQFT/SQMT). A BLANK /
// unset UOM is treated as the area default (SQF), matching buildProformaColumns
// (which renders the SQFT/SQMT rate pair when no Price UOM is set) — otherwise a
// doc with no explicit Price UOM would silently skip the SQF⇄SQM rate auto-fill.
function isAreaPriceUom(c) {
  const u = normAreaUom(c);
  return u === '' || u === 'SQF' || u === 'SQM';
}
// → { salesRate (in docPriceUom), alternateRate (in the other area UOM) }.
export function resolveItemRateForDoc(rawRate, itemUomCode, docPriceUom) {
  const r = num(rawRate);
  // Stage 6CM.3 — non-area doc UOM (PCS/RFT/RMT/MTR): the item rate is taken
  // as-is in that UOM; there is no area alternate to derive.
  if (!isAreaPriceUom(docPriceUom)) return { salesRate: round4(r), alternateRate: 0 };
  const doc  = normAreaUom(docPriceUom) === 'SQM' ? 'SQM' : 'SQF';
  const other = doc === 'SQM' ? 'SQF' : 'SQM';
  const item = normAreaUom(itemUomCode);
  // When the item's own selling UOM isn't a recognised area UOM (null / blank /
  // PCS / NOS …) but the document IS area-priced, assume the item's rate is
  // already expressed in the document's area UOM so the OTHER area rate can
  // still be derived. Without this the alternate rate came back 0 — which read
  // as "the SQMT (or SQFT) rate didn't auto-fill" right after an item pick.
  const itemArea = (item === 'SQF' || item === 'SQM') ? item : doc;
  const primary = areaRate(r, itemArea, doc);
  const alt     = areaRate(r, itemArea, other);
  return {
    salesRate:     primary != null ? round4(primary) : r,
    alternateRate: alt     != null ? round4(alt)     : 0
  };
}

// Keep the SQM ⇄ SQF price pair in sync when the user OVERRIDES one of
// them in the grid. The edited field is in its own UOM; the sibling is
// re-derived with the correct area conversion (1 SQM = 10.764 SQF).
// `salesRate` is in the document Price UOM; `alternateRate` in the other.
export function syncRatePair(editedField, value, docPriceUom) {
  const v = Math.max(0, num(value));
  // Stage 6CM.3 — non-area doc UOM has a single rate column; no pair to sync.
  if (!isAreaPriceUom(docPriceUom)) return { salesRate: round4(v), alternateRate: 0 };
  const doc   = normAreaUom(docPriceUom) === 'SQM' ? 'SQM' : 'SQF';
  const other = doc === 'SQM' ? 'SQF' : 'SQM';
  if (editedField === 'alternateRate') {
    const sales = areaRate(v, other, doc);
    return { alternateRate: round4(v), salesRate: sales != null ? round4(sales) : v };
  }
  const alt = areaRate(v, doc, other);
  return { salesRate: round4(v), alternateRate: alt != null ? round4(alt) : 0 };
}

// ── basic + fabrication ─────────────────────────────────────────────
export function calculateBasicAmount({ priceUom, salesRate, alternateRate, chargeSqft, chargeSqm, chargeRft, chargeRmt, qty }) {
  const q = num(qty);
  if (q <= 0) return 0;
  const rate = num(salesRate); const alt = num(alternateRate);
  const csqft = num(chargeSqft); const csqm = num(chargeSqm);
  const crft = num(chargeRft); const crmt = num(chargeRmt);
  const uom = String(priceUom || '').toUpperCase();
  let basic = 0;
  // Stage 6CM.3 — non-area selling UOMs (mirrors the backend engine; these
  // branches only fire for PCS/RFT/RMT/MTR, SQF/SQM unchanged below).
  if (uom === 'PCS') {
    basic = rate * q;
  } else if (uom === 'RFT') {
    if (rate > 0 && crft > 0) basic = rate * crft * q;
  } else if (uom === 'RMT' || uom === 'MTR') {
    if (rate > 0 && crmt > 0) basic = rate * crmt * q;
  } else if (priceUom === 'SQM') {
    // Bill on the area AS DISPLAYED (CHRG AREA is shown at 3 dp, CLAUDE.md 5A):
    // BASIC = rate × round3(area × qty), so the line reconciles on a plain
    // calculator (displayed area × rate). Must stay identical to the backend
    // calculateBasicAmount SQM/SQF branch.
    if (rate > 0 && csqm > 0) basic = rate * round3(csqm * q);
    else if (alt > 0 && csqft > 0) basic = alt * round3(csqft * q);
  } else {
    if (rate > 0 && csqft > 0) basic = rate * round3(csqft * q);
    else if (alt > 0 && csqm > 0) basic = alt * round3(csqm * q);
  }
  return round2(basic);
}
export function calculateFabricationAmount(fabricationDetails, qty) {
  const entries = (fabricationDetails && Array.isArray(fabricationDetails.entries)) ? fabricationDetails.entries : [];
  const q = Math.max(0, num(qty));
  let total = 0;
  for (const e of entries) {
    const rate = num(e && e.rate); const count = num(e && e.count);
    if (rate > 0 && count > 0) total += rate * count * q;
  }
  return round2(total);
}

// ── GST split (intra → CGST=SGST, inter → IGST) ─────────────────────
export function determineSupplyType({ sellerStateCode, buyerStateCode, isSez, isExport }) {
  if (isExport === true) return 'export';
  if (isSez === true) return 'sez';
  const re = /^[0-9]{2}$/;
  const s = re.test(String(sellerStateCode || '')) ? String(sellerStateCode) : null;
  const b = re.test(String(buyerStateCode || '')) ? String(buyerStateCode) : null;
  if (!s || !b) return 'intra_state';
  return s === b ? 'intra_state' : 'inter_state';
}
export function calculateGstSplit({ taxableValue, gstRate, supplyType }) {
  const taxable = Math.max(0, num(taxableValue));
  const rate = Math.max(0, num(gstRate));
  const totalRaw = taxable * (rate / 100);
  if (supplyType === 'inter_state') {
    const igst = round2(totalRaw);
    return { cgstAmount: 0, sgstAmount: 0, igstAmount: igst, gstAmount: igst };
  }
  if (supplyType === 'export' || supplyType === 'sez') {
    return { cgstAmount: 0, sgstAmount: 0, igstAmount: 0, gstAmount: 0 };
  }
  const totalTax = round2(totalRaw);
  const cgst = round2(totalTax / 2);
  const sgst = round2(totalTax - cgst);
  return { cgstAmount: cgst, sgstAmount: sgst, igstAmount: 0, gstAmount: round2(cgst + sgst) };
}

// ── one line ────────────────────────────────────────────────────────
export function computeLine(line, header) {
  const l = line || {}; const h = header || {};
  const actualW = num(l.actualWidthMm); const actualH = num(l.actualHeightMm);
  const chargeW = actualW > 0 ? calculateChargeDimension(actualW, h.wastageMode, h.wastageWidthMm) : 0;
  const chargeH = actualH > 0 ? calculateChargeDimension(actualH, h.wastageMode, h.wastageHeightMm) : 0;
  const actualSqm = calculateAreaSqm(actualW, actualH);
  const chargeSqm = calculateAreaSqm(chargeW, chargeH);
  const actualSqft = calculateAreaSqft(actualSqm);
  const chargeSqft = calculateAreaSqft(chargeSqm);
  const qty = round3(l.qty);

  const basicAmount = calculateBasicAmount({
    priceUom: l.priceUomCode || h.priceUomCode,
    salesRate: l.salesRate, alternateRate: l.alternateRate,
    chargeSqft, chargeSqm, qty
  });
  // Flat (single-row) LAMI/DGU/LAMI+DGU: fabrication × glass-ply count from the
  // composed description (mirrors backend computeLine). Parent assemblies recompute
  // via applyTreeRollup, so no double-multiply; plain lines → ×1.
  const baseFabricationAmount = calculateFabricationAmount(l.fabricationDetails, qty);
  const lType = String(l.type || '').toUpperCase();
  let flatLayers = 1;
  if (lType === 'LAMI' || lType === 'DGU' || lType === 'LAMI+DGU') {
    const plies = countGlassPliesFromDesc(l.itemDesc);
    if (plies > 1) flatLayers = plies;
  }
  const fabricationAmount = round2(baseFabricationAmount * flatLayers);
  const chargeRft = calculatePerimeterRft(chargeW, chargeH);
  const chargeRmt = calculatePerimeterRmt(chargeW, chargeH);
  const valueAddedAmount = calculateValueAddedAmount(l.processPricing, {
    chargeSqft, chargeSqm, chargeRft, chargeRmt, qty,
    // Per-side edge billing (mirrors backend): the line's L1-L4 selection map,
    // the effective basis, and the charge/actual W/H the length is measured on.
    edgeDetail: l.edgeDetail,
    edgeworkBasis: h.edgeworkDimensionBasis || 'CHARGE',
    chargeWidthMm: chargeW, chargeHeightMm: chargeH,
    actualWidthMm: actualW, actualHeightMm: actualH
  });
  const otherChargeAmount = round2(l.otherChargeAmount);
  // Discount mirrors the GlassMaster grid: a % drives the amount over
  // (basic + fabrication + value-add); a directly-entered lineDiscount is a
  // fallback when no % is set.
  const discPct = num(l.discPct);
  const discountBase = basicAmount + fabricationAmount + valueAddedAmount;
  const lineDiscount = discPct > 0 ? round2(discountBase * discPct / 100) : round2(l.lineDiscount);
  const taxableAmount = round2(discountBase + otherChargeAmount - lineDiscount);
  const split = calculateGstSplit({ taxableValue: taxableAmount, gstRate: num(l.gstRate), supplyType: h.supplyType });

  return {
    ...l,
    chargeWidthMm: chargeW, chargeHeightMm: chargeH,
    actualSqm, actualSqft, chargeSqm, chargeSqft, qty,
    basicAmount, fabricationAmount, valueAddedAmount, otherChargeAmount,
    discPct, lineDiscount,
    taxableAmount,
    cgstAmount: split.cgstAmount, sgstAmount: split.sgstAmount, igstAmount: split.igstAmount,
    gstAmount: split.gstAmount,
    lineTotal: round2(taxableAmount + split.gstAmount)
  };
}

// ── Multi-level tree compute (GlassMaster nesting; mirrors backend) ──
// Money fields summed bottom-up into a parent. A line is a "billed leaf"
// when it has no children AND its own type isn't SHEET (a sheet SIZE = raw
// material, area-only) or GASFILL (no price). Parents are pure roll-ups and
// are skipped in header totals (no double-count).
const TREE_MONEY_FIELDS = [
  'basicAmount', 'fabricationAmount', 'valueAddedAmount', 'otherChargeAmount',
  'lineDiscount', 'taxableAmount', 'cgstAmount', 'sgstAmount', 'igstAmount',
  'gstAmount', 'lineTotal'
];
const NO_MONEY_TYPES = ['SHEET', 'GASFILL'];
// Glass weight: kg = thickness(mm) × 2.5 × actual area(m²)  (2.5 ≈ float-glass
// 2500 kg/m³ per mm per m²). Film / air-gap / spacer layers carry NO glass
// weight, so a LAMI / DGU / LAMI+DGU assembly sums only its glass panes.
const GLASS_DENSITY_FACTOR = 2.5;
const NON_GLASS_WEIGHT_TYPES = new Set(['INTERLAYER', 'SPACER', 'GASFILL', 'HARDWARE', 'SERVICES']);
const WEIGHT_ROLLUP_TYPES = new Set(['LAMI', 'DGU', 'LAMI+DGU']);
// Per-piece lines (HARDWARE / SERVICES): priced as rate × qty straight from the
// Item Master — no width/height, no wastage, no area, no fabrication/value-add.
// A line is a hardware/service line purely by its TYPE; everything else is
// untouched, so glass / sheet / LAMI / DGU behaviour is identical to before.
const PIECE_TYPES = ['HARDWARE', 'SERVICES'];
export function isPieceType(type) {
  return PIECE_TYPES.includes(String(type || '').toUpperCase());
}

// Material identity for sheet nesting: the stock glass, independent of the
// processes the editor appends ("… WITH FLAT POLISH", "… #CEP"). A 12mm cut can
// only be taken from a 12mm sheet, so scrap is grouped by this key.
export function sheetMaterialKey(row) {
  let d = String((row && row.itemDesc) || '').trim().toUpperCase();
  if (!d) return '—';
  d = d.split('#')[0];
  const wi = d.indexOf(' WITH ');
  if (wi >= 0) d = d.slice(0, wi);
  return d.replace(/\s+/g, ' ').trim() || '—';
}

// Stock grouping key for nesting (mirrors the backend). The Item-Master stock
// reference (`stockItemId` = source_stock_item_id || the item's own id) is
// authoritative: a toughened cut and its annealed stock sheet resolve to the
// SAME id, so the cut deducts from the sheet. Non-item / legacy rows fall back
// to the description-derived material key.
export function lineStockKey(row) {
  if (row && row.stockItemId) return 'I:' + row.stockItemId;
  return 'D:' + sheetMaterialKey(row);
}
export function lineStockLabel(row) {
  const name = row && row.stockItemName ? String(row.stockItemName).trim() : '';
  return name || sheetMaterialKey(row);
}

// Types that never consume a glass stock sheet — excluded from a SHEET master's
// cut area (interlayer / spacer / gas-fill / hardware / services / combo). SHEET
// itself is stock, not a cut.
const NON_GLASS_CUT_TYPES = new Set(['SHEET', 'INTERLAYER', 'SPACER', 'GASFILL', 'HARDWARE', 'SERVICES', 'COMBO']);

// Types nested inside a LAMI/DGU that are NOT glass plies (so they add no
// fabrication layer): the bonding/air-gap materials and any nested assembly
// parent. A glass ply may be typed T, T-DRAWING, T-BLOCK, T-SHAPE, A-DRAW, … or
// blank, so layers are counted by EXCLUSION of these types — MUST mirror the
// backend proformaCalculation NON_GLASS_LAYER_TYPES / isGlassLayer.
const NON_GLASS_LAYER_TYPES = new Set([
  'INTERLAYER', 'SPACER', 'GASFILL', 'HARDWARE', 'SERVICES', 'SHEET',
  'LAMI', 'DGU', 'LAMI+DGU', 'COMBO'
]);

// A non-glass component token inside a composed LAMI/DGU description — the
// interlayer film, the spacer, or the gas fill. MUST mirror the backend
// isNonGlassDescPart.
function isNonGlassDescPart(part) {
  const s = String(part || '').toUpperCase();
  return /\bPVB\b|\bSGP\b|\bEVA\b|INTERLAYER|SPACER|WARM\s*EDGE|DRY\s*AIR|ARGON|KRYPTON|XENON|\bGAS\b|\bAIR\b/.test(s);
}

// Count the glass plies of a LAMI/DGU entered as ONE flat row (no child pane
// rows) from its composed description. Splits the part after the first ':' on
// the layer separator " + " (NOT a bare '+', which appears inside grade suffixes
// like "(GOLD+)") and counts the parts that are not an interlayer/spacer/gas.
// Returns 0 when the text has no composed structure. MUST mirror the backend
// countGlassPliesFromDesc.
function countGlassPliesFromDesc(desc) {
  const s = String(desc || '');
  const colon = s.indexOf(':');
  const body = colon >= 0 ? s.slice(colon + 1) : s;
  const parts = body.split(/\s\+\s/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return 0;
  let plies = 0;
  for (const p of parts) if (!isNonGlassDescPart(p)) plies += 1;
  return plies;
}

// A combo (item-set) parent billed at its OWN combo rate (FIXED) instead of the
// sum of its component children. Mirrors the backend isFixedCombo.
export function isFixedComboRow(row) {
  return row
    && String(row.type || '').toUpperCase() === 'COMBO'
    && row.nestingConfig && row.nestingConfig.combo
    && String(row.nestingConfig.combo.priceMode || '').toUpperCase() === 'FIXED';
}

// Rule 1 — does a part (cw × ch mm) fit at least one stock sheet of its material?
// Rotation is allowed. No dims / no stock → not checkable (treated as fitting).
export function fitsAnySheet(cw, ch, sheets) {
  if (!(cw > 0) || !(ch > 0)) return true;
  if (!Array.isArray(sheets) || !sheets.length) return true;
  return sheets.some((s) => {
    const sw = num(s.w); const sh = num(s.h);
    if (!(sw > 0) || !(sh > 0)) return true;
    return (cw <= sw && ch <= sh) || (cw <= sh && ch <= sw);
  });
}

// One leaf row, display-ready: areas already ×qty (per-line total). SHEET
// sizes + GASFILL carry no money.
function computeLeafRow(l, h) {
  const type = String(l.type || '').toUpperCase();
  // WO-3.3 — a struck-out original / waived-cancelled cut-piece line bills nothing
  // (mirrors backend computeLine + isBilledLeaf). Treated as no-money + its
  // other-charges forced to 0 below, so the editor shows a change-revision's
  // struck / waived lines at ₹0. Inert for every ordinary LIVE line.
  const coState = String(l.lineState || '').toUpperCase();
  const coZero = coState === 'STRUCK' || (coState === 'CANCELLED' && l.chargeable === false);
  const noMoney = NO_MONEY_TYPES.includes(type) || coZero;
  // Hardware / service line: rate × qty, no dimensions/area. Discount + GST still
  // apply (discount over the basic; GST on the net), so the 3-step flow is
  // pick item → qty → optional discount.
  if (PIECE_TYPES.includes(type)) {
    const pqty = round3(l.qty);
    const basicAmount = round2(num(l.salesRate) * pqty);
    const otherChargeAmount = round2(l.otherChargeAmount);
    const discPct = num(l.discPct);
    const lineDiscount = discPct > 0 ? round2(basicAmount * discPct / 100) : round2(l.lineDiscount);
    const taxableAmount = round2(basicAmount + otherChargeAmount - lineDiscount);
    const split = calculateGstSplit({ taxableValue: taxableAmount, gstRate: num(l.gstRate), supplyType: h.supplyType });
    return {
      ...l,
      chargeWidthMm: 0, chargeHeightMm: 0,
      actualSqm: 0, actualSqft: 0, chargeSqm: 0, chargeSqft: 0,
      actualRft: 0, actualRmt: 0, chargeRft: 0, chargeRmt: 0,
      qty: pqty, basicAmount, fabricationAmount: 0, valueAddedAmount: 0,
      otherChargeAmount, discPct, lineDiscount,
      taxableAmount, cgstAmount: split.cgstAmount, sgstAmount: split.sgstAmount, igstAmount: split.igstAmount,
      gstAmount: split.gstAmount, lineTotal: round2(taxableAmount + split.gstAmount),
      weight: 0   // hardware / services carry no glass weight
    };
  }
  const aw = num(l.actualWidthMm); const ah = num(l.actualHeightMm);
  // Stage 6CM.2 — charge dims per the effective chargeable mode (absent → AUTO,
  // i.e. the prior "manual wins only when editChargeable" behaviour). LOCKED
  // modes ignore manual overrides. Mirrors the backend resolveChargeMm.
  const mcw = num(l.manualChargeWidthMm); const mch = num(l.manualChargeHeightMm);
  const cw = resolveChargeMm(aw, h.chargeableMode, h.editChargeable === true, mcw, h.wastageMode, h.wastageWidthMm);
  const ch = resolveChargeMm(ah, h.chargeableMode, h.editChargeable === true, mch, h.wastageMode, h.wastageHeightMm);
  const chargeSqmU = calculateAreaSqm(cw, ch); const chargeSqftU = calculateAreaSqft(chargeSqmU);
  const actualSqmU = calculateAreaSqm(aw, ah); const actualSqftU = calculateAreaSqft(actualSqmU);
  const qty = round3(l.qty);
  const priceUom = l.priceUomCode || h.priceUomCode;
  // Stage 6CM.3 — perimeter computed before basic so an RFT/RMT/MTR price UOM
  // bills on running length (SQF/SQM ignore these inputs).
  const chargeRftU = calculatePerimeterRft(cw, ch); const chargeRmtU = calculatePerimeterRmt(cw, ch);
  const basicAmount = noMoney ? 0 : calculateBasicAmount({
    priceUom, salesRate: l.salesRate, alternateRate: l.alternateRate,
    chargeSqft: chargeSqftU, chargeSqm: chargeSqmU, chargeRft: chargeRftU, chargeRmt: chargeRmtU, qty
  });
  // Flat (single-row) LAMI/DGU/LAMI+DGU: fabrication bills × the glass-ply count
  // parsed from the composed description. When the assembly is built as a PARENT
  // with child pane rows instead, this leaf value is discarded + recomputed by
  // applyTreeRollup (real pane count), so no double-multiply. MUST mirror the
  // backend computeLine. A plain glass line → 0 plies → ×1.
  const baseFabricationAmount = noMoney ? 0 : calculateFabricationAmount(l.fabricationDetails, qty);
  let flatAssemblyLayers = 1;
  if (type === 'LAMI' || type === 'DGU' || type === 'LAMI+DGU') {
    const plies = countGlassPliesFromDesc(l.itemDesc);
    if (plies > 1) flatAssemblyLayers = plies;
  }
  const fabricationAmount = round2(baseFabricationAmount * flatAssemblyLayers);
  const valueAddedAmount = noMoney ? 0 : calculateValueAddedAmount(l.processPricing, {
    chargeSqft: chargeSqftU, chargeSqm: chargeSqmU, chargeRft: chargeRftU, chargeRmt: chargeRmtU, qty,
    // Per-side edge billing (mirrors backend + computeLine): L1-L4 selection map,
    // effective basis, and the charge/actual W/H the running length measures on.
    edgeDetail: l.edgeDetail,
    edgeworkBasis: h.edgeworkDimensionBasis || 'CHARGE',
    chargeWidthMm: cw, chargeHeightMm: ch,
    actualWidthMm: aw, actualHeightMm: ah
  });
  // Glass weight (kg) for this line: thickness × 2.5 × actual area (m²), using
  // the per-line total area (× qty). Non-glass layers (film / spacer / gas) and
  // piece lines carry no weight; a LAMI/DGU parent's weight is the Σ of its
  // glass panes (handled in applyTreeRollup).
  const weight = NON_GLASS_WEIGHT_TYPES.has(type)
    ? 0
    : round2(num(l.thicknessMm) * GLASS_DENSITY_FACTOR * actualSqmU * qty);
  const otherChargeAmount = coZero ? 0 : round2(l.otherChargeAmount);
  const discPct = num(l.discPct);
  const discountBase = basicAmount + fabricationAmount + valueAddedAmount;
  const lineDiscount = coZero ? 0 : (discPct > 0 ? round2(discountBase * discPct / 100) : round2(l.lineDiscount));
  const taxableAmount = round2(discountBase + otherChargeAmount - lineDiscount);
  const split = calculateGstSplit({ taxableValue: taxableAmount, gstRate: num(l.gstRate), supplyType: h.supplyType });
  return {
    ...l,
    chargeWidthMm: cw, chargeHeightMm: ch,
    actualSqm: round4(actualSqmU * qty), actualSqft: round4(actualSqftU * qty),
    chargeSqm: round4(chargeSqmU * qty), chargeSqft: round4(chargeSqftU * qty),
    actualRft: round4(calculatePerimeterRft(aw, ah) * qty), actualRmt: round4(calculatePerimeterRmt(aw, ah) * qty),
    chargeRft: round4(chargeRftU * qty), chargeRmt: round4(chargeRmtU * qty),
    qty, basicAmount, fabricationAmount, valueAddedAmount, otherChargeAmount, discPct, lineDiscount,
    taxableAmount, cgstAmount: split.cgstAmount, sgstAmount: split.sgstAmount, igstAmount: split.igstAmount,
    gstAmount: split.gstAmount, lineTotal: round2(taxableAmount + split.gstAmount),
    weight,
    // Glass-ply multiplier for a FLAT (single-row) LAMI/DGU. The fabrication
    // summary multiplies the master's fab count by this. A parent-with-children
    // assembly instead carries parentFabLayers (set in applyTreeRollup).
    fabLayers: flatAssemblyLayers > 1 ? flatAssemblyLayers : undefined
  };
}

// Roll children up into parents (deepest first), child-priced. SHEET masters
// also get cut/scrap/utilization aggregates; LAMI/DGU keep their own (synced)
// dimensions and only sum money from their layer children.
export function applyTreeRollup(rows, supplyType, priceUomCode) {
  // SHEET scrap and parent pricing bill on the document's Price UOM area: a
  // SQM-priced document uses the SQM areas, otherwise SQFT. Keeps the nesting
  // bar + scrap value consistent with the rest of the grid.
  const sqmPriced = ['SQM', 'SQMT'].includes(String(priceUomCode || '').toUpperCase());
  const arr = (Array.isArray(rows) ? rows : []).slice();
  const idxByRef = new Map();
  arr.forEach((l, i) => { if (l && l.refUuid) idxByRef.set(l.refUuid, i); });
  const childIdx = new Map();
  arr.forEach((l, i) => {
    if (l && l.parentRefUuid) { const a = childIdx.get(l.parentRefUuid) || []; a.push(i); childIdx.set(l.parentRefUuid, a); }
  });
  const parents = arr.filter((l) => l && l.refUuid && childIdx.has(l.refUuid));
  parents.sort((a, b) => num(b.level) - num(a.level));   // deepest first
  for (const p of parents) {
    const i = idxByRef.get(p.refUuid);
    const kids = (childIdx.get(p.refUuid) || []).map((k) => arr[k]);
    const sum = (f) => kids.reduce((s, k) => s + num(k[f]), 0);
    const out = { ...arr[i], salesRate: 0, alternateRate: 0 };
    for (const f of TREE_MONEY_FIELDS) out[f] = round2(sum(f));
    if (String(out.type || '').toUpperCase() === 'SHEET') {
      // Look-through nesting (mirrors the backend): a SHEET master owns its
      // descendant STOCK sheets + the glass CUT pieces taken from them, INCLUDING
      // panes buried in a LAMI/DGU assembly. Stock = the master's own dims + every
      // SHEET-type descendant; cuts = every glass LEAF descendant (non-SHEET,
      // non interlayer/spacer/gas/hardware/service). Assembly parents are skipped
      // (not leaves) — their panes are the real cuts. Areas are per-line totals
      // (already × qty).
      const m = arr[i];
      const isLeaf = (n) => !childIdx.has(n.refUuid);
      const descendants = [];
      {
        const stack = (childIdx.get(m.refUuid) || []).slice();
        while (stack.length) {
          const di = stack.pop(); const node = arr[di];
          if (!node) continue;
          descendants.push(node);
          for (const k of (childIdx.get(node.refUuid) || [])) stack.push(k);
        }
      }
      const stockRows = descendants.filter((d) => String(d.type || '').toUpperCase() === 'SHEET');
      const cutRows = descendants.filter((d) => isLeaf(d) && !NON_GLASS_CUT_TYPES.has(String(d.type || '').toUpperCase()));
      // Group stock + cuts by the resolved STOCK identity. Same-material SHEET
      // stock always merges into ONE group even across different sizes and even
      // if one row carries an Item-Master id and another does not — a stock sheet
      // IS its material (12mm bronze annealed sheet is the same stock whatever the
      // panel size). Cut→sheet matching (toughened cut deducting from its annealed
      // stock via the shared source_stock_item_id) is preserved via an alias map:
      // each stock row's id-key is aliased onto its merged material group, so a cut
      // keyed by that id still lands in the right group. MUST mirror the backend.
      const groups = new Map();
      const grp = (k) => { let g = groups.get(k); if (!g) { g = { label: null, sheetSqft: 0, sheetSqm: 0, cutSqft: 0, cutSqm: 0, sheetCount: 0, sheets: [], gstRate: null, oversized: 0 }; groups.set(k, g); } return g; };
      const addSheetDim = (g, r) => {
        const w = num(r.actualWidthMm); const h = num(r.actualHeightMm);
        // One {w,h} per physical sheet (honour qty) so the distinct-size
        // aggregation below counts sheets correctly. Mirrors the backend.
        if (w > 0 && h > 0) { const q = Math.max(1, Math.round(num(r.qty)) || 1); for (let i = 0; i < q; i += 1) g.sheets.push({ w, h }); }
        if (g.gstRate == null && r.gstRate != null) g.gstRate = num(r.gstRate);
      };
      // Merged group key for a SHEET stock row = its material identity, so distinct
      // sizes / records of the same stock collapse. `aliasByStockId` maps each stock
      // row's raw id-key (I:<id>) → that merged material key, so cuts follow it.
      const aliasByStockId = new Map();
      const stockGroupKey = (r) => {
        const mk = 'M:' + sheetMaterialKey(r);
        const raw = lineStockKey(r);
        if (raw.charAt(0) === 'I') aliasByStockId.set(raw, mk);
        return mk;
      };
      const addStock = (r) => {
        const g = grp(stockGroupKey(r));
        // Mirror backend: nesting bills on CHARGEABLE area (chargeSqft/chargeSqm),
        // falling back to actual when a row has no chargeable area.
        g.sheetSqft += num(r.chargeSqft || r.actualSqft); g.sheetSqm += num(r.chargeSqm || r.actualSqm);
        // Physical sheet count (Σ qty), matching the backend — a ×3 stock row is 3 sheets.
        g.sheetCount += Math.max(1, Math.round(num(r.qty)) || 1);
        if (!g.label) g.label = lineStockLabel(r); addSheetDim(g, r);
      };
      const sheetKeys = new Set();
      if (num(m.actualSqft) > 0) { addStock(m); sheetKeys.add(stockGroupKey(m)); }
      for (const s of stockRows) { addStock(s); sheetKeys.add(stockGroupKey(s)); }
      // A description-only cut with no matching stock falls back to the sole sheet
      // (single-material case). A cut with a stock id follows the alias onto the
      // merged material group of the sheet that shares that id.
      const onlyKey = sheetKeys.size === 1 ? [...sheetKeys][0] : null;
      // Resolve a cut to its merged stock group:
      //   1. a cut with a stock id follows the alias onto the sheet sharing that id
      //      (toughened cut → its annealed stock);
      //   2. otherwise a same-material sheet group exists → deduct from it (this is
      //      what makes a plain 12mm cut meet the merged 12mm sheet group);
      //   3. else, single-sheet documents fall back to the sole sheet.
      for (const c of cutRows) {
        const raw = lineStockKey(c);
        // Item-linked cut (raw = 'I:<stockId>', i.e. the cut carries an Item-Master
        // stock identity via source_stock_item_id): it nests ONLY on the stock sheet
        // it is physically cut from (matched through aliasByStockId). If that stock
        // sheet is NOT on the document the cut is a DIFFERENT material — e.g. a CLEAR
        // toughened pane on an ST 167-only sheet — and is IGNORED entirely: no cut
        // area / scrap / utilisation on any sheet. Two different materials must never
        // nest on one sheet just because it is the only one present.
        // Description-only cuts (no stock id, raw = 'D:…', legacy documents built
        // without Item-Master links) keep the prior fallback so they are unaffected.
        let k;
        if (raw.charAt(0) === 'I') {
          if (!aliasByStockId.has(raw)) { c.sheetOversized = false; continue; }   // no matching stock sheet → ignore
          k = aliasByStockId.get(raw);                                            // id-linked cut → its stock group
        } else {
          const matKey = 'M:' + sheetMaterialKey(c);
          if (sheetKeys.has(matKey)) k = matKey;                                  // same-material sheet exists → deduct
          else if (onlyKey != null) k = onlyKey;                                  // single-sheet fallback (legacy)
          else k = matKey;                                                        // no matching sheet → own bucket
        }
        const g = grp(k);
        // CHARGEABLE cut area (mirrors sheet basis above); fall back to actual.
        g.cutSqft += num(c.chargeSqft || c.actualSqft); g.cutSqm += num(c.chargeSqm || c.actualSqm);
        if (!g.label) g.label = lineStockLabel(c);
        if (g.gstRate == null && c.gstRate != null) g.gstRate = num(c.gstRate);
        // Rule 1 — a part must fit at least one sheet of its material (rotation ok).
        const fits = fitsAnySheet(num(c.actualWidthMm), num(c.actualHeightMm), g.sheets);
        c.sheetOversized = !fits;
        if (!fits) g.oversized += 1;
      }
      let sheetSqft = 0; let sheetSqm = 0; let cutSqft = 0; let cutSqm = 0; let scrapSqft = 0; let scrapSqm = 0; let sheetCount = 0; let oversizedCuts = 0;
      const breakdown = [];
      for (const [key, g] of groups) {
        const sc = Math.max(0, g.sheetSqft - g.cutSqft);
        sheetSqft += g.sheetSqft; sheetSqm += g.sheetSqm; cutSqft += g.cutSqft; cutSqm += g.cutSqm; sheetCount += g.sheetCount; oversizedCuts += g.oversized;
        scrapSqft += sc; scrapSqm += Math.max(0, g.sheetSqm - g.cutSqm);
        // Skip empty buckets (e.g. the unnamed "—" placeholder group).
        if (g.sheetSqft <= 0 && g.cutSqft <= 0) continue;
        const rawUtil = g.sheetSqft > 0 ? Math.round((g.cutSqft / g.sheetSqft) * 1000) / 10 : 0;
        const scSqm = Math.max(0, g.sheetSqm - g.cutSqm);
        // Distinct sheet sizes for THIS material (mirrors the backend) so the print
        // can list each material's own sizes separately.
        const gSizeMap = new Map();
        for (const sh of g.sheets) {
          const w = Math.round(num(sh.w)); const h = Math.round(num(sh.h));
          if (w <= 0 || h <= 0) continue;
          const kk = `${w}x${h}`; const e = gSizeMap.get(kk);
          if (e) e.qty += 1; else gSizeMap.set(kk, { w, h, qty: 1 });
        }
        breakdown.push({
          material: g.label || key, sheetCount: g.sheetCount,
          sizes: [...gSizeMap.values()],
          sheetSqft: round4(g.sheetSqft), cutSqft: round4(g.cutSqft), scrapSqft: round4(sc),
          sheetSqm: round4(g.sheetSqm), cutSqm: round4(g.cutSqm), scrapSqm: round4(scSqm),
          utilization: Math.min(100, rawUtil),                 // Rule 2 — cap at 100%
          overCut: rawUtil > 100, oversized: g.oversized,
          gstRate: g.gstRate != null ? g.gstRate : num(out.gstRate)
        });
      }
      // Distinct sheet sizes with physical qty across all materials (entry
      // order); persisted on nestingConfig (JSONB) for the print nesting strip.
      // Identical w×h collapse into one {w,h,qty}. Mirrors the backend rollup.
      const sizeMap = new Map();
      for (const [, g] of groups) {
        for (const s of g.sheets) {
          const w = Math.round(num(s.w)); const h = Math.round(num(s.h));
          if (w <= 0 || h <= 0) continue;
          const key = `${w}x${h}`;
          const e = sizeMap.get(key);
          if (e) e.qty += 1; else sizeMap.set(key, { w, h, qty: 1 });
        }
      }
      out.nestingConfig = { ...(out.nestingConfig || {}), sheetSizes: [...sizeMap.values()] };
      // The master row keeps its OWN dimensions/area/qty (mirrors GlassMaster's
      // rebuildTree). The aggregate (per-material scrap) lives in these fields.
      out.sheetCount = sheetCount;
      out.sheetSqft = round4(sheetSqft); out.sheetSqm = round4(sheetSqm);
      out.cutSqft = round4(cutSqft); out.cutSqm = round4(cutSqm);
      out.scrapSqft = round4(scrapSqft); out.scrapSqm = round4(scrapSqm);
      out.utilization = sheetSqft > 0 ? Math.min(100, Math.round((cutSqft / sheetSqft) * 1000) / 10) : 0;   // Rule 2
      out.oversizedCuts = oversizedCuts;
      // Sheet cost mode (Stage 6Q.3) — a SHEET part group bills EITHER its leftover
      // scrap (WASTAGE, the default + every legacy document) OR a price on the sheet
      // line itself (SHEET_PRICE, the manual parentRate path below). The two are
      // mutually exclusive: WASTAGE suppresses the parent price, SHEET_PRICE
      // suppresses the scrap value. Areas/utilisation stay computed for display in
      // both modes — only the billable money is gated.
      const sheetCostMode = resolveSheetCostMode(out.nestingConfig);
      // Per-material scrap pricing — each material carries its own scrap rate
      // (nestingConfig.scrapRates[material]); the legacy single rate applies only
      // when the group has exactly one material. Skipped entirely in SHEET_PRICE
      // mode (the sheet line is priced instead).
      const rates = (out.nestingConfig && out.nestingConfig.scrapRates) || {};
      const singleMat = breakdown.length === 1;
      let scrapValueTotal = 0;
      for (const b of breakdown) {
        let rate = sheetCostMode === 'SHEET_PRICE' ? 0 : num(rates[b.material]);
        if (!(rate > 0) && singleMat && sheetCostMode !== 'SHEET_PRICE') rate = num(out.scrapRate);
        b.scrapRate = rate > 0 ? rate : null;
        // Scrap value bills on the Price-UOM area (rate is ₹ per that unit), on the
        // 3-dp DISPLAYED scrap area (CLAUDE.md rules 4 & 5A) so "displayed area × rate"
        // reconciles on a calculator and the printed rate equals the entered rate.
        // MUST stay in lockstep with backend proformaCalculation.service.js.
        b.scrapValue = rate > 0 ? round2(rate * round3(sqmPriced ? b.scrapSqm : b.scrapSqft)) : 0;
        scrapValueTotal += b.scrapValue;
      }
      out.materialBreakdown = breakdown;
      out.scrapValue = round2(scrapValueTotal);
      out.sheetCostMode = sheetCostMode;
    } else if (isFixedComboRow(out)) {
      // Fixed-price combo: billed at its OWN combo rate (× qty); components become
      // an informational BOM excluded from the totals. SUM combos need no special
      // case — the generic Σ-children rollup above already bills each component.
      const cRate = num(out.nestingConfig.combo.comboRate);
      const cQty = num(out.qty) > 0 ? num(out.qty) : 1;
      const basic = round2(cRate * cQty);
      const oc = round2(out.otherChargeAmount);
      const discPct = num(out.discPct);
      const disc = discPct > 0 ? round2(basic * discPct / 100) : round2(out.lineDiscount);
      const taxable = round2(basic + oc - disc);
      const split = calculateGstSplit({ taxableValue: taxable, gstRate: num(out.gstRate), supplyType: supplyType || 'intra_state' });
      out.salesRate = round4(cRate);
      out.basicAmount = basic; out.fabricationAmount = 0; out.valueAddedAmount = 0;
      out.lineDiscount = disc; out.taxableAmount = taxable;
      out.cgstAmount = split.cgstAmount; out.sgstAmount = split.sgstAmount; out.igstAmount = split.igstAmount;
      out.gstAmount = split.gstAmount; out.lineTotal = round2(taxable + split.gstAmount);
      out.comboBilled = true;
      for (const k of kids) k.comboNoBill = true;
    }
    // Glazed assembly weight: a LAMI / DGU / LAMI+DGU bills the SUM of its glass
    // panes' weights. Non-glass children (film / spacer / gas) already carry 0,
    // so a plain Σ ignores them. Deepest-first ordering means a nested LAMI
    // inside a DGU is already rolled up before the DGU sums it.
    if (WEIGHT_ROLLUP_TYPES.has(String(out.type || '').toUpperCase())) {
      out.weight = round2(kids.reduce((s, k) => s + num(k.weight), 0));
    }
    // Stage 6Q.2 — LAMI / DGU / LAMI+DGU parent display roll-ups (the structural
    // line_type stays in `type`; these are derived for the grid + print):
    //   • combination ₹/area rate = Σ layer rates (all layers share the area)
    //   • total thickness          = Σ every layer's thickness (glass + film/spacer)
    //   • TYPE label (glassTypeCode) = temper-derived A-/T-/T+A+ <assembly>
    if (isLamiOrDgu(out.type)) {
      out.salesRate = round2(kids.reduce((s, k) => s + num(k.salesRate), 0));
      out.thicknessMm = round2(kids.reduce((s, k) =>
        s + (num(k.thicknessMm) > 0 ? num(k.thicknessMm) : num(parseThicknessMm(k.itemDesc))), 0));
      const panes = [];
      let paneMaxGst = 0;
      const stack = (childIdx.get(out.refUuid) || []).slice();
      while (stack.length) {
        const node = arr[stack.pop()];
        if (!node) continue;
        const kids = childIdx.get(node.refUuid) || [];
        // A glass ply = a LEAF descendant whose type is not a non-glass material
        // or a nested assembly parent (counts T / T-DRAWING / T-BLOCK / T-SHAPE /
        // A-DRAW / blank). Mirrors the backend isGlassLayer.
        const isLeafPly = kids.length === 0
          && !NON_GLASS_LAYER_TYPES.has(String(node.type || '').toUpperCase());
        if (isLeafPly) {
          // Migration 0132 — carry per-ply fabrication applicability (default TRUE)
          // so the parent-fabrication multiplier can skip opted-out laminated plies.
          panes.push({ text: node.itemDesc, glassTypeCode: node.glassTypeCode, fabApplicable: node.fabApplicable !== false,
            // Per-ply Shape / Template flags drive the (S)/(T) marker on the
            // assembly TYPE label (display-only; assemblyTypeLabel prepends them).
            shape: !!(node.nestingConfig && node.nestingConfig.shape),
            template: !!(node.nestingConfig && node.nestingConfig.template) });
          if (num(node.gstRate) > paneMaxGst) paneMaxGst = num(node.gstRate);
        }
        for (const k of kids) stack.push(k);
      }
      out.glassTypeCode = assemblyTypeLabel(out.type, panes) || out.glassTypeCode;
      // Mirror the assembly's fabrication onto each glass-pane child for DISPLAY
      // only: a hole / cut-out drilled through the unit physically passes through
      // every pane, so the tree should SHOW the same fab counts on each layer.
      // This sets `mirroredFabEntries` on the child VIEW objects (arr[ci]) — it
      // does NOT add money (billing stays on the parent's parentFab below), so
      // there is no double-count. Only DIRECT glass-pane children are mirrored.
      const parentFabEntries = (out.fabricationDetails && Array.isArray(out.fabricationDetails.entries))
        ? out.fabricationDetails.entries.filter((e) => e && num(e.count) > 0)
        : [];
      if (parentFabEntries.length) {
        for (const ci of (childIdx.get(out.refUuid) || [])) {
          const child = arr[ci];
          if (!child) continue;
          const isGlassChild = !childIdx.has(child.refUuid)
            && !NON_GLASS_LAYER_TYPES.has(String(child.type || '').toUpperCase());
          // Only mirror the fab onto plies the parent fabrication actually applies
          // to (migration 0132) — an opted-out ply shows no mirrored fab, matching
          // that it is excluded from the billed multiplier below.
          if (isGlassChild && child.fabApplicable !== false) arr[ci] = { ...child, mirroredFabEntries: parentFabEntries };
        }
      }
      // Fabrication entered on the assembly PARENT bills once per glass pane:
      // amount = (Σ rate × count × qty) × number of glass layers, taxed at the
      // assembly's GST rate (falls back to the highest pane rate). Added on top
      // of the Σ-children rollup (panes carry no fabrication of their own), and
      // re-counted in computeProformaPreview since parents are otherwise skipped.
      // Multiplier = number of ENABLED glass plies (migration 0132). Default =
      // every ply, so an untouched laminate is unchanged. Disabling all plies
      // yields 0 (no fabrication billed). The 1-fallback only guards the
      // degenerate "no glass plies detected" case (unchanged behaviour).
      const glassLayers = panes.length === 0 ? 1 : panes.filter((p) => p.fabApplicable !== false).length;
      // A STRUCK / waived (Change-Order) assembly parent bills ₹0 — skip its
      // parent-level fabrication (mirrors computeLeafRow's coZero gate + the backend
      // applyParentRollup, so a decomposed/cancelled laminate leaks no fab money).
      const outCoState = String(out.lineState || '').toUpperCase();
      const outCoZero = outCoState === 'STRUCK' || (outCoState === 'CANCELLED' && out.chargeable === false);
      const baseFab = outCoZero ? 0 : calculateFabricationAmount(out.fabricationDetails, out.qty);
      const parentFab = round2(baseFab * glassLayers);
      if (parentFab > 0) {
        const fabGst = num(out.gstRate) > 0 ? num(out.gstRate) : (paneMaxGst > 0 ? paneMaxGst : 18);
        const sp = calculateGstSplit({ taxableValue: parentFab, gstRate: fabGst, supplyType });
        out.fabricationAmount = round2(num(out.fabricationAmount) + parentFab);
        out.taxableAmount = round2(num(out.taxableAmount) + parentFab);
        out.cgstAmount = round2(num(out.cgstAmount) + sp.cgstAmount);
        out.sgstAmount = round2(num(out.sgstAmount) + sp.sgstAmount);
        out.igstAmount = round2(num(out.igstAmount) + sp.igstAmount);
        out.gstAmount = round2(num(out.gstAmount) + sp.gstAmount);
        out.lineTotal = round2(num(out.lineTotal) + parentFab + sp.gstAmount);
        out.parentFabAmount = parentFab;
        out.parentFabLayers = glassLayers;
        out.parentFabGstRate = fabGst;   // rate the GST was split at (for HSN-group GST)
        out.parentFabCgst = sp.cgstAmount; out.parentFabSgst = sp.sgstAmount;
        out.parentFabIgst = sp.igstAmount; out.parentFabGst = sp.gstAmount;
      }
    }
    // Manual PARENT price: when a roll-up parent (SHEET / LAMI / DGU / LAMI+DGU)
    // carries a manual rate (nestingConfig.parentRate), bill rate × the parent's
    // own area (in the Price UOM) on TOP of the child sum. Stored separately so the
    // document totals can add it even though parents are otherwise skipped. Skipped
    // for fixed-combo parents (they carry their own combo price). For a SHEET part
    // group the price applies ONLY in SHEET_PRICE mode — in WASTAGE mode the sheet
    // bills its scrap instead, so the parent price is suppressed even if a stray
    // rate is present. Non-SHEET assemblies (LAMI/DGU) keep their prior behaviour.
    const isSheetParent = String(out.type || '').toUpperCase() === 'SHEET';
    const sheetPriceAllowed = !isSheetParent
      || resolveSheetCostMode(out.nestingConfig) === 'SHEET_PRICE';
    const parentRate = num(out.nestingConfig && out.nestingConfig.parentRate);
    if (parentRate > 0 && !out.comboBilled && sheetPriceAllowed) {
      // chargeSqm / chargeSqft already include qty (computeLeafRow stores ×qty
      // totals), so the price is rate × area — multiplying by qty again would
      // double-count it (e.g. 1000 × 89.304 = 89,304 for a qty-10 sheet).
      const pArea = sqmPriced ? num(out.chargeSqm) : num(out.chargeSqft);
      const pBasic = round2(parentRate * pArea);
      if (pBasic > 0) {
        const sp = calculateGstSplit({ taxableValue: pBasic, gstRate: num(out.gstRate), supplyType });
        out.parentPriceAmount = pBasic;
        out.parentPriceGstRate = num(out.gstRate);   // rate the GST was split at (for HSN-group GST)
        out.basicAmount = round2(num(out.basicAmount) + pBasic);
        out.taxableAmount = round2(num(out.taxableAmount) + pBasic);
        out.cgstAmount = round2(num(out.cgstAmount) + sp.cgstAmount);
        out.sgstAmount = round2(num(out.sgstAmount) + sp.sgstAmount);
        out.igstAmount = round2(num(out.igstAmount) + sp.igstAmount);
        out.gstAmount = round2(num(out.gstAmount) + sp.gstAmount);
        out.lineTotal = round2(num(out.lineTotal) + pBasic + sp.gstAmount);
        out.parentPriceCgst = sp.cgstAmount; out.parentPriceSgst = sp.sgstAmount;
        out.parentPriceIgst = sp.igstAmount; out.parentPriceGst = sp.gstAmount;
      }
    }
    arr[i] = out;
  }
  return arr;
}

// Compute every line into a display-ready tree row (per-row money/area +
// parent roll-ups + SHEET scrap). Used by BOTH the grid and the preview.
export function computeTreeRows(header, lines) {
  const h = header || {};
  const supplyType = h.supplyType || determineSupplyType({
    sellerStateCode: h.companyStateCode, buyerStateCode: h.placeOfSupplyStateCode,
    isSez: h.isSez, isExport: h.isExport
  });
  const hForCalc = { ...h, supplyType };
  const leaves = (Array.isArray(lines) ? lines : [])
    .map((l, i) => computeLeafRow({ ...l, lineNo: l.lineNo != null ? l.lineNo : i + 1 }, hForCalc));
  return { rows: applyTreeRollup(leaves, supplyType, h.priceUomCode), supplyType };
}

// ── document roll-up (live preview) ─────────────────────────────────
// Document-level discount (applied BEFORE tax, on the taxable subtotal). A
// percentage drives the amount when > 0; otherwise a flat amount is used.
// Clamped to [0, subtotal]. MUST mirror backend proformaCalculation.service.js.
export function resolveDocDiscount(subTotal, header) {
  const h = header && typeof header === 'object' ? header : {};
  const base = round2(num(subTotal));
  const pct = num(h.docDiscountPercent);
  let amount = pct > 0 ? round2(base * pct / 100) : round2(num(h.docDiscountAmount));
  amount = Math.max(0, Math.min(amount, base));
  return { percent: pct > 0 ? pct : 0, amount };
}

// GST split for a manual charge (mirrors backend gstCalculator.calculateGstSplit):
// intra → CGST+SGST (CGST rounded first, SGST = remainder), inter → IGST,
// export/SEZ (zero-rated) → no tax.
function manualGstSplit(taxable, gstRate, supplyType) {
  const total = round2(Math.max(0, num(taxable)) * Math.max(0, num(gstRate)) / 100);
  if (supplyType === 'inter_state') return { cgst: 0, sgst: 0, igst: total, gst: total };
  if (supplyType === 'intra_state') {
    const cgst = round2(total / 2);
    return { cgst, sgst: round2(total - cgst), igst: 0, gst: total };
  }
  return { cgst: 0, sgst: 0, igst: 0, gst: 0 };   // zero-rated (export / SEZ)
}

// Document-level MANUAL other charges (ad-hoc rows picked from the Manual Charge
// Master). Amount = Area × Rs (blank/zero Area = 1 → flat lump-sum). Each row is
// a TAXABLE supply at its own custom GST %. MUST mirror backend
// proformaCalculation.service.js resolveManualCharges.
export function resolveManualCharges(header, supplyType = 'intra_state') {
  const list = Array.isArray(header && header.manualOtherCharges) ? header.manualOtherCharges : [];
  const rows = [];
  let amount = 0, cgst = 0, sgst = 0, igst = 0;
  for (const m of list) {
    if (!m) continue;
    const area = num(m.area);
    const rate = num(m.rate);
    const gstRate = num(m.gstRate);
    const label = (m.label == null ? '' : String(m.label)).trim();
    const qty = area > 0 ? area : 1;
    const amt = round2(qty * rate);
    if (amt === 0 && !label) continue;
    const sp = manualGstSplit(amt, gstRate, supplyType);
    amount += amt;
    cgst += sp.cgst; sgst += sp.sgst; igst += sp.igst;
    rows.push({
      label: label || null, area: round3(area), rate: round2(rate), gstRate: round2(gstRate),
      amount: amt, cgstAmount: sp.cgst, sgstAmount: sp.sgst, igstAmount: sp.igst, gstAmount: sp.gst
    });
  }
  return {
    rows, amount: round2(amount),
    cgstAmount: round2(cgst), sgstAmount: round2(sgst), igstAmount: round2(igst),
    gstAmount: round2(cgst + sgst + igst)
  };
}

// GST at the (HSN, rate) GROUP level — MUST mirror backend proformaCalculation
// sumGstByRateGroup + the print _buildGstRateSummary (CLAUDE.md rule 4). Tax each
// (HSN/SAC, rate) group's TAXABLE once instead of summing per-line rounded GST, so
// the editor total equals the saved / printed HSN-wise summary (taxable×rate) —
// ₹27,662.16 × 18% = ₹4,979.19, not the ₹4,979.21 a per-line sum produced.
// `contributors` = [{ hsnSac, gstRate, taxableAmount }] (billed leaves, SHEET scrap,
// LAMI/DGU parent fabrication + parent price).
export function sumGstByRateGroup(contributors, supplyType) {
  const groups = new Map();
  const order = [];
  for (const c of (Array.isArray(contributors) ? contributors : [])) {
    const taxable = num(c && c.taxableAmount);
    if (taxable === 0) continue;
    const rate = num(c && c.gstRate);
    const hsn = String((c && c.hsnSac) || '');
    const key = `${hsn}||${rate}`;
    let g = groups.get(key);
    if (!g) { g = { hsnSac: hsn, gstRate: rate, taxableAmount: 0 }; groups.set(key, g); order.push(key); }
    g.taxableAmount = round2(g.taxableAmount + taxable);
  }
  let cgst = 0, sgst = 0, igst = 0;
  for (const key of order) {
    const g = groups.get(key);
    const split = calculateGstSplit({ taxableValue: g.taxableAmount, gstRate: g.gstRate, supplyType });
    cgst += num(split.cgstAmount); sgst += num(split.sgstAmount); igst += num(split.igstAmount);
  }
  return {
    cgstAmount: round2(cgst), sgstAmount: round2(sgst), igstAmount: round2(igst),
    gstAmount: round2(cgst + sgst + igst)
  };
}

export function computeProformaPreview({ header, lines }) {
  const { rows, supplyType } = computeTreeRows(header, lines);
  // Billed leaves only: no children, and own type isn't SHEET-size / GASFILL.
  const parentSet = new Set(rows.filter((l) => l && l.parentRefUuid).map((l) => l.parentRefUuid));
  // Combo overrides: a FIXED-combo PARENT bills once (comboBilled); its component
  // children are an informational BOM that must NOT be billed (comboNoBill).
  const isBilled = (l) => {
    if (l.comboNoBill === true) return false;
    if (l.comboBilled === true) return true;
    return !parentSet.has(l.refUuid) && !NO_MONEY_TYPES.includes(String(l.type || '').toUpperCase());
  };

  const t = {
    lineCount: rows.filter((l) => isBilled(l) && (num(l.qty) > 0 || num(l.basicAmount) > 0)).length,
    totalQty: 0, totalChargeSqm: 0, totalChargeSqft: 0,
    basicAmount: 0, fabricationAmount: 0, valueAddedAmount: 0, otherChargeAmount: 0,
    discountAmount: 0,
    subTotalAmount: 0, docDiscountPercent: 0, docDiscountAmount: 0,
    taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0, gstAmount: 0
  };
  // Collect every taxable supply as a { hsnSac, gstRate, taxableAmount } contributor,
  // then tax each (HSN, rate) GROUP once (sumGstByRateGroup) — mirrors backend
  // rollupTotals so the editor total matches the saved / printed HSN summary.
  const contribs = [];
  for (const l of rows) {
    if (!isBilled(l)) continue;
    t.totalQty += num(l.qty);
    t.totalChargeSqm += num(l.chargeSqm);     // already ×qty
    t.totalChargeSqft += num(l.chargeSqft);
    t.basicAmount += num(l.basicAmount);
    t.fabricationAmount += num(l.fabricationAmount);
    t.valueAddedAmount += num(l.valueAddedAmount);
    t.otherChargeAmount += num(l.otherChargeAmount);
    t.discountAmount += num(l.lineDiscount);
    t.taxableAmount += num(l.taxableAmount);
    contribs.push({ hsnSac: l.hsnSac, gstRate: num(l.gstRate), taxableAmount: num(l.taxableAmount) });
  }
  // SHEET masters are roll-up parents (skipped above), but their scrap value is a
  // real billable amount — add it (taxed at the SHEET line's HSN + rate, matching
  // the print's sheet-nesting GST row) on top.
  t.scrapAmount = 0;
  for (const l of rows) {
    const bd = Array.isArray(l.materialBreakdown) ? l.materialBreakdown : null;
    if (!bd) continue;
    // Rule 3 — each material's scrap keeps its OWN GST rate: one contributor per
    // material (mirrors backend rollupTotals).
    for (const b of bd) {
      const sv = num(b.scrapValue);
      if (sv <= 0) continue;
      t.scrapAmount += sv;
      t.basicAmount += sv;
      t.taxableAmount += sv;
      contribs.push({ hsnSac: l.hsnSac, gstRate: num(b.gstRate), taxableAmount: sv });
    }
  }
  t.scrapAmount = round2(t.scrapAmount);
  // LAMI/DGU assembly parents are roll-up rows (skipped by isBilled above), but
  // fabrication entered on the parent (× glass layers) is a real billable charge
  // — add it (at the parent HSN + the rate it was split at) on top.
  for (const l of rows) {
    const pf = num(l.parentFabAmount);
    if (pf <= 0) continue;
    t.fabricationAmount += pf;
    t.taxableAmount += pf;
    contribs.push({ hsnSac: l.hsnSac, gstRate: num(l.parentFabGstRate) || num(l.gstRate), taxableAmount: pf });
  }
  // Manual PARENT price (added on top of the child sum) — counted here because the
  // parent roll-up row itself is skipped by isBilled above.
  for (const l of rows) {
    const pp = num(l.parentPriceAmount);
    if (pp <= 0) continue;
    t.basicAmount += pp;
    t.taxableAmount += pp;
    contribs.push({ hsnSac: l.hsnSac, gstRate: num(l.parentPriceGstRate) || num(l.gstRate), taxableAmount: pp });
  }
  t.totalQty = round3(t.totalQty);
  t.totalChargeSqm = round4(t.totalChargeSqm);
  t.totalChargeSqft = round4(t.totalChargeSqft);
  ['basicAmount', 'fabricationAmount', 'valueAddedAmount', 'otherChargeAmount', 'discountAmount', 'taxableAmount']
    .forEach((k) => { t[k] = round2(t[k]); });
  // Document-level discount BEFORE tax (mirrors backend rollupTotals): deduct from
  // the taxable subtotal and scale each taxable contributor by the same ratio, THEN
  // tax the (HSN, rate) groups.
  t.subTotalAmount = t.taxableAmount;            // taxable BEFORE doc discount
  const _docDisc = resolveDocDiscount(t.taxableAmount, header);
  t.docDiscountPercent = _docDisc.percent;
  t.docDiscountAmount = _docDisc.amount;
  let _taxedContribs = contribs;
  if (_docDisc.amount > 0 && t.taxableAmount > 0) {
    const ratio = (t.taxableAmount - _docDisc.amount) / t.taxableAmount;
    t.taxableAmount = round2(t.taxableAmount - _docDisc.amount);
    _taxedContribs = contribs.map((c) => ({ ...c, taxableAmount: round2(num(c.taxableAmount) * ratio) }));
  }
  const _baseGst = sumGstByRateGroup(_taxedContribs, supplyType);
  t.cgstAmount = _baseGst.cgstAmount;
  t.sgstAmount = _baseGst.sgstAmount;
  t.igstAmount = _baseGst.igstAmount;
  t.gstAmount = _baseGst.gstAmount;
  // Stage 6CM.2 — honour Company Defaults round-off (absent → NEAREST = prior).
  // Manual ad-hoc charges (custom GST %) are taxable supplies added AFTER the doc
  // discount (never discounted). Fold value + GST into the totals.
  const _manual = resolveManualCharges(header, supplyType);
  t.manualOtherCharges = _manual.rows;
  t.manualChargesAmount = _manual.amount;
  t.manualChargesGst = _manual.gstAmount;
  t.taxableAmount = round2(t.taxableAmount + _manual.amount);
  t.cgstAmount = round2(t.cgstAmount + _manual.cgstAmount);
  t.sgstAmount = round2(t.sgstAmount + _manual.sgstAmount);
  t.igstAmount = round2(t.igstAmount + _manual.igstAmount);
  t.gstAmount = round2(t.gstAmount + _manual.gstAmount);
  const preRound2 = round2(t.taxableAmount + t.gstAmount);
  const ro = applyDocRoundOff(preRound2, { roundOffEnabled: header.roundOffEnabled, roundOffMode: header.roundOffMode });
  t.roundOffAmount = ro.roundOffAmount;
  t.grandTotal = ro.grandTotal;
  return { lines: rows, totals: t, supplyType };
}

// ── Live auto Other-Charges reconciliation (SINGLE source of truth) ──────────
// The base preview `totals` already carry any OC that was persisted onto the
// LINES (a reloaded proforma). The bottom panel's rule engine reports the
// CURRENT live OC (`liveOtherCharges = { amount, gst, ready }`). Both the
// "Details & Summary" popup AND the bottom-panel running total must show ONE
// identical figure that also matches the saved / printed total, so they both
// call THIS helper instead of each layering OC their own way.
//
// To reflect the true total WITHOUT double-counting on a reloaded document we
// add only the DELTA (live − already-in-`totals`) and never subtract (a removed
// or disabled OC self-corrects on save). On a saved document in steady state the
// live figure equals the persisted OC, so the delta is 0 and every surface shows
// the persisted grand total — the same number Jasper prints. GST of the delta is
// taken from the engine's OC GST and split by the document supply type.
export function reconcileProformaTotals(totals, liveOtherCharges, supplyType, roundOff) {
  const t = totals || {};
  const oc = liveOtherCharges || { amount: 0, gst: 0, ready: false };
  const ocInT = round2(num(t.otherChargeAmount));
  const addNet = oc.ready ? Math.max(0, round2(num(oc.amount) - ocInT)) : 0;
  const addGst = (oc.ready && num(oc.amount) > 0)
    ? round2(num(oc.gst) * (addNet / num(oc.amount))) : 0;

  let cgst = num(t.cgstAmount), sgst = num(t.sgstAmount), igst = num(t.igstAmount);
  if (supplyType === 'intra_state') { cgst = round2(cgst + addGst / 2); sgst = round2(sgst + addGst / 2); }
  else if (supplyType === 'inter_state') { igst = round2(igst + addGst); }

  const gstAmount = round2(cgst + sgst + igst);
  const taxableAmount = round2(num(t.taxableAmount) + addNet);
  // Grand-total round-off must be re-derived on the FINAL (taxable + GST) total,
  // not carried from the base document's round-off with the OC delta appended. The
  // base `t.grandTotal` already had round-off applied to the pre-OC total, so
  // layering the delta on top of it leaks that round-off and yields a non-integer
  // grand total (₹1,75,773.14 instead of the printed ₹1,75,773.00). This only bites
  // when a delta is present — i.e. on FIRST load, before the persisted per-line OC
  // is reflected in the base totals; in steady edit-mode state the delta is 0 and
  // the base grand total is already correct. When the caller supplies the round-off
  // policy (Company Defaults: enabled + NEAREST/UP/DOWN) we recompute here so the
  // live editor total lands on the same rupee the backend persists and Jasper
  // prints. Legacy caller (no policy) keeps the prior additive behaviour.
  let grandTotal;
  let roundOffAmount = num(t.roundOffAmount);
  if (roundOff && (addNet !== 0 || addGst !== 0)) {
    const rr = applyDocRoundOff(round2(taxableAmount + gstAmount), roundOff);
    grandTotal = rr.grandTotal;
    roundOffAmount = rr.roundOffAmount;
  } else {
    grandTotal = round2(num(t.grandTotal) + addNet + addGst);
  }

  return {
    ...t,
    otherChargeAmount: round2(ocInT + addNet),
    subTotalAmount: round2(num(t.subTotalAmount) + addNet),
    taxableAmount,
    cgstAmount: cgst, sgstAmount: sgst, igstAmount: igst,
    gstAmount,
    roundOffAmount,
    grandTotal,
    // The OC delta actually layered on top (net + its GST) — for captions.
    liveOtherChargesAdded: addNet,
    liveOtherChargesGstAdded: addGst
  };
}

// ── Tree nesting engine (GlassMaster-style: refUuid + parentRefUuid + level) ──
let _uidSeq = 0;
export function createUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_e) { /* fall through */ }
  _uidSeq += 1;
  return 'r_' + _uidSeq + '_' + Math.random().toString(36).slice(2, 10);
}

export function blankLine(lineNo, level, parentRefUuid) {
  return {
    refUuid: createUuid(), parentRefUuid: parentRefUuid || null, level: level || 0,
    isExpanded: true,
    lineNo: lineNo || 1, itemId: null, itemDesc: '',
    // Stock identity for nesting — resolved from the Item Master at item-pick
    // time (source_stock_item_id || the item's own id). Lets a toughened cut
    // deduct from its annealed stock sheet without parsing the description.
    stockItemId: null, stockItemName: null,
    rawItemDesc: '', processPricing: [], edgeDetail: {},
    type: '', location: '',
    materialTypeCode: null, glassTypeCode: null, thicknessCode: null, thicknessMm: null, categoryCode: null,
    hsnSac: null, gstRate: 18, taxability: null,
    actualWidthMm: 0, actualHeightMm: 0, qty: 0,
    priceUomCode: null, salesRate: 0, alternateRate: 0,
    // Stage 6PL-1c — where this line's rate came from. NULL until the row is
    // actually priced (item pick, or the server's backfill on save).
    priceListId: null, priceSource: null,
    manualChargeWidthMm: 0, manualChargeHeightMm: 0,
    discPct: 0, lineDiscount: 0, remarks1: '', remarks2: '', remarks3: '', remarks4: '', remarks5: '',
    fabricationDetails: { entries: [] }, otherChargeAmount: 0,
    narration: '', scrapRate: null, nestingConfig: {},
    // Per-layer fabrication applicability (migration 0132). TRUE by default; a
    // laminated glass ply can opt out via the combination window so the parent
    // fabrication (holes/cutouts) skips it in the pane multiplier.
    fabApplicable: true
  };
}

// End index (exclusive) of a row's whole descendant block — contiguous because
// the array is pre-order. Used for insert-below / delete-subtree.
export function descEnd(rows, idx) {
  const p = rows[idx];
  if (!p) return idx;
  let i = idx + 1;
  while (i < rows.length && num(rows[i].level) > num(p.level)) i += 1;
  return i;
}

// Insert a blank row relative to `refUuid` and re-number the tree.
//   "above" → a same-level sibling immediately before the target.
//   "below" → a same-level sibling AFTER the target's whole subtree …
//   …EXCEPT below a SHEET MASTER (a top-level SHEET that already has children):
//   there a same-level sibling would land after the part sizes AND, when given
//   the same material, spawn a SECOND independent nesting group. Instead we add
//   a new stock sheet SIZE as the FIRST child — it sits directly under the
//   master and stays inside the one nesting group. This is the permanent fix for
//   "Add Row Below groups separately / lands after the part sizes".
export function insertRelative(rows, refUuid, where) {
  const arr = Array.isArray(rows) ? rows.slice() : [];
  const idx = arr.findIndex((r) => r && r.refUuid === refUuid);
  if (idx < 0) return arr;
  const base = arr[idx];
  const isSheetMaster = String(base.type || '').toUpperCase() === 'SHEET' && !base.parentRefUuid;
  const hasChildren = arr.some((r) => r && r.parentRefUuid === base.refUuid);
  if (where === 'below' && isSheetMaster && hasChildren) {
    const nr = blankLine(1, num(base.level) + 1, base.refUuid);
    nr.location = base.location || '';
    nr.type = 'SHEET';
    nr.itemDesc = base.itemDesc || '';
    nr.gstRate = num(base.gstRate);
    if (base.priceUomCode) nr.priceUomCode = base.priceUomCode;
    arr[idx] = { ...base, isExpanded: true };
    arr.splice(idx + 1, 0, nr);             // FIRST child, directly below the master
    return rebuildTree(arr);
  }
  const at = where === 'below' ? descEnd(arr, idx) : idx;
  const nr = blankLine(1, base.level, base.parentRefUuid);
  nr.location = base.location || '';
  arr.splice(at, 0, nr);
  return rebuildTree(arr);
}

// Reconstruct the editor tree fields (refUuid / parentRefUuid / level /
// isExpanded) from persisted lines (line_no / parentLineNo / nestLevel). Used
// when loading a saved proforma back into the grid.
export function hydrateLines(apiLines) {
  const arr = Array.isArray(apiLines) ? apiLines : [];
  const refByLineNo = new Map();
  const withRef = arr.map((l) => {
    // Migration 0169 — reuse the server's stable ref_uuid so a re-save matches each
    // line to its persisted row (Change-Order fate is re-derived server-side). Fall
    // back to a fresh id only for a line the server never persisted one for (legacy).
    const refUuid = l.refUuid || createUuid();
    refByLineNo.set(l.lineNo != null ? l.lineNo : null, refUuid);
    return { ...l, refUuid };
  });
  return withRef.map((l) => ({
    ...l,
    parentRefUuid: l.parentLineNo != null ? (refByLineNo.get(l.parentLineNo) || null) : null,
    level: num(l.nestLevel),
    // Nested groups (SHEET / LAMI / DGU) open COLLAPSED when a saved proforma is
    // reopened, so the editor lands on a compact top-level view. New/inserted rows
    // (blankLine / insertRelative) still default expanded — only load collapses.
    isExpanded: false,
    fabricationDetails: l.fabricationDetails || { entries: [] },
    processPricing: Array.isArray(l.processPricing) ? l.processPricing : [],
    edgeDetail: (l.edgeDetail && typeof l.edgeDetail === 'object') ? l.edgeDetail : {},
    nestingConfig: (l.nestingConfig && typeof l.nestingConfig === 'object') ? l.nestingConfig : {},
    // Per-layer fabrication applicability (migration 0132) — restored from the
    // saved line so an opted-out laminated ply survives reopen. Default TRUE.
    fabApplicable: l.fabApplicable !== false,
    // Stage 6PL-1c — the price-list stamp travels with the reopened line so a
    // re-save keeps its provenance instead of being re-stamped MANUAL.
    priceListId: l.priceListId || null,
    priceSource: l.priceSource || null
  }));
}

// Recompute display row numbers ("1", "1.1", "1.2", "2") + hasChildren from the
// pre-order array + parentRefUuid links. Pure structure (no money).
export function rebuildTree(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const childCount = new Map();
  for (const r of list) if (r && r.parentRefUuid) childCount.set(r.parentRefUuid, (childCount.get(r.parentRefUuid) || 0) + 1);
  const numByRef = {}; const seenByParent = {};
  let top = 0;
  return list.map((r) => {
    let rowNo;
    if (!r.parentRefUuid) { top += 1; rowNo = String(top); }
    else {
      const pNum = numByRef[r.parentRefUuid] || '?';
      seenByParent[r.parentRefUuid] = (seenByParent[r.parentRefUuid] || 0) + 1;
      rowNo = pNum + '.' + seenByParent[r.parentRefUuid];
    }
    numByRef[r.refUuid] = rowNo;
    const hasChildren = (childCount.get(r.refUuid) || 0) > 0;
    // Preserve object identity when nothing structural changed: cloning every
    // row on each keystroke churns memory (GC pressure) and busts the grid's
    // GridRow memo, forcing all visible rows to re-render. Returning the SAME
    // ref for unchanged rows keeps inserts O(changed rows), not O(all rows).
    if (r.rowNo === rowNo && r.hasChildren === hasChildren) return r;
    return { ...r, rowNo, hasChildren };
  });
}

// Keep a SHEET master's stock-size children grouped directly below it: SHEET
// children come first (in their existing order), then any other children (cut
// pieces / blanks). Stable, subtree-preserving — only reorders DIRECT children
// of a SHEET node, leaving LAMI/DGU and everything else untouched. Returns the
// SAME row objects (identity preserved) so callers can no-op when nothing moved.
export function groupSheetChildren(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length < 2) return arr;
  const byRef = new Map(arr.map((r) => [r.refUuid, r]));
  const kids = new Map();   // parentRef → [childRef…] in original order
  const roots = [];
  for (const r of arr) {
    if (r.parentRefUuid && byRef.has(r.parentRefUuid)) {
      const a = kids.get(r.parentRefUuid) || []; a.push(r.refUuid); kids.set(r.parentRefUuid, a);
    } else {
      roots.push(r.refUuid);
    }
  }
  const isSheet = (r) => r && String(r.type || '').toUpperCase() === 'SHEET';
  const out = [];
  const visit = (ref) => {
    const node = byRef.get(ref);
    if (!node) return;
    out.push(node);
    let childRefs = kids.get(ref) || [];
    if (isSheet(node) && childRefs.length) {
      const sheetK = childRefs.filter((c) => isSheet(byRef.get(c)));
      const otherK = childRefs.filter((c) => !isSheet(byRef.get(c)));
      if (sheetK.length && otherK.length) childRefs = sheetK.concat(otherK);   // stable group
    }
    for (const c of childRefs) visit(c);
  };
  for (const r of roots) visit(r);
  return out.length === arr.length ? out : arr;   // safety: never drop rows
}

// Visible list honouring expand/collapse: a row is hidden if any ancestor is
// collapsed. Pre-order array + level make this a single stack pass.
export function getVisible(rows) {
  const out = []; const st = [];
  for (const r of (Array.isArray(rows) ? rows : [])) {
    const lvl = num(r.level);
    while (st.length > lvl) st.pop();
    if (st.every(Boolean)) out.push(r);
    st[lvl] = r.isExpanded !== false;
  }
  return out;
}

function mapPayloadLine(l, lineNo, parentLineNo) {
  return {
    // Migration 0169 — stable per-line identity. Sent so update() matches each
    // line to its persisted row and re-derives the Change-Order fate server-side.
    refUuid: l.refUuid || null,
    lineNo, itemId: l.itemId || null, itemDesc: l.itemDesc || '',
    // Persist the stock-item identity inside item_snapshot (the backend surfaces
    // stockItemId/stockItemName back from it on load). Without this the SHEET
    // material key flips on reopen (stockItemName → description) and the per-material
    // scrap rate no longer resolves, zeroing the wastage value. Preserve any
    // snapshot already carried from a prior load; overlay the current identity.
    itemSnapshot: {
      ...(l.itemSnapshot && typeof l.itemSnapshot === 'object' && !Array.isArray(l.itemSnapshot) ? l.itemSnapshot : {}),
      stockItemId: l.stockItemId || null,
      stockItemName: l.stockItemName || null
    },
    type: l.type ? String(l.type) : null,
    rawItemDesc: l.rawItemDesc || null,
    processPricing: Array.isArray(l.processPricing) ? l.processPricing
      .filter((p) => p && p.name && num(p.rate) > 0)
      .map((p) => ({ name: String(p.name), rate: num(p.rate), uom: String(p.uom || '') })) : [],
    // Per-side (L1-L4) edge selection for RFT/RMT processes. Sent as-is; the
    // backend re-resolves the running length and bills the selected edges.
    edgeDetail: (l.edgeDetail && typeof l.edgeDetail === 'object' && !Array.isArray(l.edgeDetail)) ? l.edgeDetail : {},
    materialTypeCode: l.materialTypeCode || null, glassTypeCode: l.glassTypeCode || null,
    thicknessCode: l.thicknessCode || null, thicknessMm: l.thicknessMm != null ? num(l.thicknessMm) : null,
    categoryCode: l.categoryCode || null, polishTypeCode: l.polishTypeCode || null, hsnSac: l.hsnSac || null,
    gstRate: num(l.gstRate), taxability: l.taxability || null,
    actualWidthMm: num(l.actualWidthMm), actualHeightMm: num(l.actualHeightMm),
    manualChargeWidthMm: num(l.manualChargeWidthMm), manualChargeHeightMm: num(l.manualChargeHeightMm),
    qty: num(l.qty),
    priceUomCode: l.priceUomCode || null, salesRate: num(l.salesRate), alternateRate: num(l.alternateRate),
    // Stage 6PL-1c — the price-list stamp. Round-tripped so reopening and
    // re-saving a document does not lose which list priced each line.
    priceListId: l.priceListId || null, priceSource: l.priceSource || null,
    discPct: num(l.discPct), lineDiscount: num(l.lineDiscount),
    remarks1: l.remarks1 ? String(l.remarks1) : null, remarks2: l.remarks2 ? String(l.remarks2) : null,
    remarks3: l.remarks3 ? String(l.remarks3) : null, remarks4: l.remarks4 ? String(l.remarks4) : null, remarks5: l.remarks5 ? String(l.remarks5) : null,
    fabricationDetails: l.fabricationDetails || { entries: [] },
    // Per-layer fabrication applicability (migration 0132) — sent as-is; default
    // TRUE. The backend re-derives the parent-fabrication multiplier from it.
    fabApplicable: l.fabApplicable !== false,
    // Tree nesting — parentLineNo + nestLevel are re-derived at save so they
    // match the renumbered line_no the backend stores (survives delete+reinsert).
    parentLineNo, nestLevel: num(l.level),
    narration: l.narration ? String(l.narration) : null,
    scrapRate: l.scrapRate != null ? num(l.scrapRate) : null,
    nestingConfig: (l.nestingConfig && typeof l.nestingConfig === 'object') ? l.nestingConfig : {},
    // WO-3.3 — Change-Order fate flags MUST round-trip so a revision's struck /
    // cancelled lines survive an editor save (else they would silently revert to
    // normal billed lines). Absent/default (LIVE / null / true / null / false) on
    // every ordinary line, so this is inert for normal proformas.
    lineState: l.lineState || 'LIVE',
    cancelDisp: l.cancelDisp || null,
    chargeable: l.chargeable !== false,
    changeOrderId: l.changeOrderId || null,
    isCommitted: l.isCommitted === true
  };
}

// Persist the tree: keep meaningful rows (a parent is kept if it has children),
// assign line_no in pre-order, and link each child to its parent's NEW line_no
// via a refUuid→line_no map (multi-level safe; no contiguity assumption).
function buildPayloadLines(lines) {
  const arr = Array.isArray(lines) ? lines : [];
  const childRefs = new Set();
  for (const l of arr) if (l && l.parentRefUuid) childRefs.add(l.parentRefUuid);
  const keep = (l) => childRefs.has(l.refUuid)           // a parent is always kept
    || (l.itemDesc && String(l.itemDesc).trim()) || num(l.qty) > 0 || num(l.actualWidthMm) > 0;
  const kept = arr.filter(keep);
  const keptRefs = new Set(kept.map((l) => l.refUuid));
  const lineNoByRef = new Map();
  kept.forEach((l, i) => lineNoByRef.set(l.refUuid, i + 1));
  return kept.map((l, i) => {
    // Parent reference only survives if the parent itself survived the filter.
    const parentLineNo = (l.parentRefUuid && keptRefs.has(l.parentRefUuid))
      ? lineNoByRef.get(l.parentRefUuid) : null;
    return mapPayloadLine({ ...l, level: parentLineNo ? num(l.level) : 0 }, i + 1, parentLineNo);
  });
}

export function buildSavePayload(header, lines) {
  return {
    proformaDate: header.proformaDate,
    customerId: header.customerId || null,
    billTo: header.billTo || {},
    shipTo: header.shipTo || {},
    placeOfSupplyStateCode: header.placeOfSupplyStateCode || null,
    companyStateCode: header.companyStateCode || null,
    wastageMode: header.wastageMode || 'ROUND_OFF',
    wastageWidthMm: num(header.wastageWidthMm),
    wastageHeightMm: num(header.wastageHeightMm),
    inputUnitCode: header.inputUnitCode || 'MM',
    priceUomCode: header.priceUomCode || 'SQF',
    areaMode: header.areaMode || 'SQFT',
    isSez: header.isSez === true,
    isExport: header.isExport === true,
    editChargeable: header.editChargeable === true,
    // Per-document Other-Charge value overrides ({ ruleId: value }). Sanitised
    // + clamped server-side; the engine still respects each rule's
    // allow_runtime_rate, so locked charges ignore a stored override.
    otherChargeOverrides: (header.otherChargeValues && typeof header.otherChargeValues === 'object') ? header.otherChargeValues : {},
    // Document-level discount (before tax): % drives the amount; a flat amount
    // is the fallback when no % is set. Backend re-resolves + clamps.
    docDiscountPercent: num(header.docDiscountPercent),
    docDiscountAmount: num(header.docDiscountAmount),
    // Document-level manual ad-hoc charges ({ label, area, rate }). Backend
    // re-derives amount = area × rate (no GST) and clamps the list.
    manualOtherCharges: Array.isArray(header.manualOtherCharges)
      ? header.manualOtherCharges.map((m) => ({
        label: m && m.label != null ? String(m.label) : null,
        area: num(m && m.area), rate: num(m && m.rate), gstRate: num(m && m.gstRate)
      }))
      : [],
    // Free-text operator remarks (bottom-panel tabs) — saved with the document.
    proformaRemarks: header.proformaRemarks || '',
    workOrderRemarks: header.workOrderRemarks || '',
    // Free-text PROJECT reference (a.k.a. "Project Ref" in the details popup).
    projectName: header.projectName || null,
    // Migration 0185 — the document's own price-list choice travels with it.
    priceListMode: header.priceListMode || 'AUTO',
    priceListOverrideId: header.priceListMode === 'FIXED' ? (header.priceListOverrideId || null) : null,
    // Order-detail metadata (Details & Summary popup).
    templatesReceived: header.templatesReceived || null,
    poNo: header.poNo || null,
    poDate: header.poDate || null,
    dispatchRoute: header.dispatchRoute || null,
    dispatchMode: header.dispatchMode || null,
    workType: header.workType || null,
    paymentTerms: header.paymentTerms || null,
    // Express-delivery flag + 0-5 priority rating (persist across edits).
    expressDelivery: header.expressDelivery === true,
    expressDeliveryRating: Number(header.expressDeliveryRating) || 0,
    // Stage SALES-PROFORMA-1 — HRMS salesperson. The backend resolves the
    // effective source (customer mapping vs manual override) + enforces
    // mandatory-to-save; a manual override requires the reason.
    salespersonEmployeeId: header.salespersonEmployeeId || null,
    salespersonOverrideReason: header.salespersonOverrideReason || null,
    lines: buildPayloadLines(lines)
  };
}

// ── Stage SALES-PROFORMA-1 — salesperson helpers (pure) ──
export function formatSalespersonSource(source) {
  return ({
    CUSTOMER_MAPPING: 'Customer Mapping',
    MANUAL_OVERRIDE: 'Manual Override',
    NONE: 'Not Mapped'
  })[source] || 'Not Mapped';
}

// Salesperson is MANDATORY to save a proforma draft (confirmed business
// rule). A manual override additionally requires a reason. Returns
// { ok, error } for a pre-save gate that mirrors the backend enforcement.
export function validateSalespersonForSave(header) {
  const h = header || {};
  if (!h.salespersonEmployeeId) {
    return { ok: false, error: 'A salesperson is required. Map one on the customer or pick a manual override.' };
  }
  if (h.salespersonSource === 'MANUAL_OVERRIDE' && !String(h.salespersonOverrideReason || '').trim()) {
    return { ok: false, error: 'A reason is required to override the customer salesperson.' };
  }
  return { ok: true, error: null };
}

// ── Stage SALES-PROFORMA-AUDIT-FIX-1 — state-lifecycle helpers (pure) ──
// These fix the audited frontend statefulness bugs. They are pure functions
// so the same logic drives the components AND the unit tests. NONE of them
// touch pricing, GST, or line-item business logic.

// Fix 1 — Place of Supply must follow the CURRENTLY selected customer, derived
// from that customer's billing snapshot. It is NEVER carried over from a prior
// pick (the old `h.placeOfSupplyStateCode || …` OR-preserve could keep customer
// A's state after switching to customer B → a wrong CGST/SGST↔IGST split).
export function placeOfSupplyForCustomer(billTo) {
  return (billTo && billTo.placeOfSupplyStateCode) || null;
}

// Fix 2 — hydrate the persisted salesperson when reopening a saved proforma so a
// manual override survives an open → edit-unrelated-field → save round-trip.
// Without this the field silently re-defaults to the customer mapping and the
// next save re-resolves to the mapping, discarding the override. Source defaults
// to NONE when absent (e.g. a legacy row saved before the salesperson stage).
export function salespersonStateFromApi(data) {
  const d = data || {};
  return {
    salespersonEmployeeId: d.salespersonEmployeeId || null,
    salespersonSource: d.salespersonSource || 'NONE',
    salespersonOverrideReason: d.salespersonOverrideReason || null
  };
}

// Fix 3 — when the customer genuinely CHANGES, drop a manual override so the new
// customer never inherits the previous customer's salesperson. The initial load
// (prevCustomerId == null) and a same-customer re-resolve preserve the header, so
// this composes correctly with Fix 2 (a reopened override is kept, not cleared).
export function clearOverrideOnCustomerChange(header, prevCustomerId, newCustomerId) {
  const h = header || {};
  const changed = prevCustomerId != null && prevCustomerId !== newCustomerId;
  if (changed && h.salespersonSource === 'MANUAL_OVERRIDE') {
    return { ...h, salespersonEmployeeId: null, salespersonSource: 'NONE', salespersonOverrideReason: null };
  }
  return h;
}

// Fix 4 — navigation-guard predicate. Block leaving the editor ONLY when there
// are unsaved user edits, the doc isn't read-only, and a save isn't already in
// flight (a save in flight is about to clear the dirty state).
export function shouldBlockNavigation({ dirty, saving, readOnly } = {}) {
  return dirty === true && saving !== true && readOnly !== true;
}

export function formatMoney(n) {
  const v = num(n);
  return '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Tag colour per lifecycle status. PENDING blue (active/awaiting), CONFIRMED
// green (accepted), HOLD orange (paused), CANCELLED red (terminal).
export function statusColor(status) {
  return ({ PENDING: 'blue', CONFIRMED: 'green', HOLD: 'orange', CANCELLED: 'red' })[status] || 'default';
}

// Title-case display label. Unknown / legacy values echo through unchanged.
export function statusLabel(status) {
  return ({ PENDING: 'Pending', CONFIRMED: 'Confirmed', HOLD: 'Hold', CANCELLED: 'Cancelled' })[status]
    || (status == null ? '' : String(status));
}

// Pure UI-state map that drives the editor action bar + read-only behaviour for
// a given status. Keeps button visibility unit-testable and identical to the
// backend transition rules.
//   PENDING   → editable; Save / Confirm / Hold / Cancel
//   HOLD      → editable; Save / Confirm / Move to Pending / Cancel
//   CONFIRMED → read-only; Hold / Cancel
//   CANCELLED → read-only; (view only)
export function proformaStatusUi(status) {
  switch (status) {
    case 'CONFIRMED':
      return { readOnly: true, canSave: false, canConfirm: false, canHold: true, canReopen: false, canCancel: true };
    case 'HOLD':
      return { readOnly: false, canSave: true, canConfirm: true, canHold: false, canReopen: true, canCancel: true };
    case 'CANCELLED':
      return { readOnly: true, canSave: false, canConfirm: false, canHold: false, canReopen: false, canCancel: false };
    case 'PENDING':
    default:
      return { readOnly: false, canSave: true, canConfirm: true, canHold: true, canReopen: false, canCancel: true };
  }
}

// ── Stage SALES-PROFORMA-UI-FLOW-1 — permission + editor action visibility ──
// PURE, unit-testable. `me.permissions` is a flat key list; the master role
// carries the `*` wildcard (mirrors the ItemMasterPage convention). Never a
// source of truth on its own — the backend re-checks every permission.
export function hasPermission(permissions, key) {
  const list = Array.isArray(permissions) ? permissions : [];
  return list.includes('*') || list.includes(key);
}

// Which editor action buttons are visible for a given document + permission set.
// Driven ENTIRELY by the status lifecycle (proformaStatusUi) + whether the doc is
// saved (has an id), so the button set can never diverge from the lifecycle rules:
//   • new (unsaved)                    → Save PI (no Update / Delete / transitions)
//   • existing PENDING/HOLD (editable) → Update + Delete + transitions
//   • existing CONFIRMED (read-only)   → Hold / Cancel (no Update / Delete)
//   • existing CANCELLED (read-only)   → PI List only
// Save PI and Update are mutually exclusive (saveNew XOR update). Print + PI List
// are always available on a saved doc; PI List is always shown.
export function proformaEditorActions(header, permissions) {
  const status = header && header.status;
  const isNew = !(header && header.id);
  const ui = proformaStatusUi(status);
  const canDeletePerm = hasPermission(permissions, 'erp.sales.proforma.delete');
  const canCreatePerm = hasPermission(permissions, 'erp.sales.proforma.create');
  return {
    piList: true,                                  // back-to-list, always
    detailsSummary: !ui.readOnly,                  // salesperson / order-detail entry (editable + new)
    print: !isNew,                                 // saved doc only
    saveNew: isNew && ui.canSave,                  // NEW → Save PI
    update: !isNew && ui.canSave,                  // existing editable → Update (same id)
    // Stage SALES-PROFORMA-CLONE-1 — Duplicate a saved doc into a new PENDING
    // draft. Available for ANY saved status (incl. CONFIRMED / CANCELLED); needs
    // the create permission since it creates a new proforma.
    clone: !isNew && canCreatePerm,
    delete: !isNew && ui.canSave && canDeletePerm, // existing editable + permission → Delete
    confirm: !isNew && ui.canConfirm,
    hold: !isNew && ui.canHold,
    reopen: !isNew && ui.canReopen,
    cancel: !isNew && ui.canCancel
  };
}

// ── spreadsheet padding (GlassMaster-style "lots of blank rows" feel) ──
export function blankLines(count) {
  const n = Math.max(1, num(count));
  const out = [];
  for (let i = 0; i < n; i += 1) out.push(blankLine(i + 1));
  return out;
}
export function padLines(lines, minRows) {
  const rows = Array.isArray(lines) && lines.length ? lines.slice() : [];
  const min = Math.max(1, num(minRows));
  while (rows.length < min) rows.push(blankLine(rows.length + 1));
  return rows.map((l, i) => ({ ...l, lineNo: i + 1 }));
}

// ── grouped grid header descriptor (drives the GlassMaster spreadsheet) ──
// PURE data so the grouped-header contract can be unit-tested without
// rendering React. The live AntD columns are derived from this in the grid
// component; FABRICATION sub-columns are master-driven at render time.
export const PROFORMA_GRID_GROUPS = [
  { key: 'type', label: 'TYPE', cols: ['type'] },
  { key: 'desc', label: 'ITEM DESC', cols: ['itemDesc'] },
  { key: 'actMm', label: 'ACT (MM)', cols: ['actualWidthMm', 'actualHeightMm'] },
  { key: 'chgMm', label: 'CHRG (MM)', cols: ['chargeWidthMm', 'chargeHeightMm'] },
  { key: 'qty', label: 'QTY', cols: ['qty'] },
  { key: 'actArea', label: 'ACT AREA', cols: ['actualSqft', 'actualSqm'] },
  { key: 'chgArea', label: 'CHRG AREA', cols: ['chargeSqft', 'chargeSqm'] },
  { key: 'fab', label: 'FABRICATION', cols: ['fabrication'] },
  { key: 'price', label: 'PRICE', cols: ['salesRate', 'alternateRate'] },
  { key: 'basic', label: 'BASIC', cols: ['basicAmount'] },
  { key: 'valueAdd', label: 'VALUE ADD', cols: ['valueAddedAmount'] },
  { key: 'discount', label: 'DISCOUNT', cols: ['discPct', 'lineDiscount'] },
  { key: 'otherChg', label: 'OTHER CHG', cols: ['otherChargeAmount'] },
  { key: 'gst', label: 'GST', cols: ['gstRate', 'gstAmount'] },
  // TOTAL group removed 2026-08-06 — the per-line TOTAL column is hidden from the grid.
  { key: 'remarks', label: 'REMARKS', cols: ['remarks1', 'remarks2', 'remarks3', 'remarks4', 'remarks5'] }
];
export function proformaGridGroupLabels() {
  return PROFORMA_GRID_GROUPS.map((g) => g.label);
}

// ── master-driven fabrication column binding (H / C / SP / BH / CSK …) ──
// Columns + their rates come from the Process / Fabrication master
// (dropdowns.fabrications). Nothing here is a hardcoded business rate.
// Pull a short tag from a master display name like "Holes (H)" → "H".
function fabShortTag(name) {
  const s = name == null ? '' : String(name);
  const m = /\(([^)]{1,12})\)\s*$/.exec(s);
  return m ? m[1].trim() : '';
}

// Which fabrications become grid columns: only the ACTIVE ones from the
// Fabrication Master, kept in the master's own sequence (the lookup is
// ordered by fab_id). All 15 are delivered to the grid; toggling a row's
// Active flag off in the master hides its column. Each column is headed by
// the abbreviation (else its "(…)" name tag, else the fab-id code).
export function selectGridFabrications(fabrications) {
  const list = Array.isArray(fabrications) ? fabrications : [];
  return list.filter((f) => f && f.isActive !== false);
}
export function buildFabricationColumns(fabrications, max = 5) {
  const list = Array.isArray(fabrications) ? fabrications : [];
  return list
    .map((f) => {
      // Accept either the generic shape (code/abbr/rate) or the fabrication
      // master lookup shape (fabId/fabRefName/fabName/defaultPrice).
      const code = f.code || f.value || f.fabricationCode || f.abbr || f.fabRefName
        || (f.fabId != null ? `F${f.fabId}` : null);
      const label = f.abbreviation || f.abbr || f.shortLabel || fabShortTag(f.fabName) || f.label || f.name || f.fabRefName || code || '';
      const rate = num(
        f.rate != null ? f.rate
          : f.defaultRate != null ? f.defaultRate
            : f.ratePerUnit != null ? f.ratePerUnit
              : f.defaultPrice
      );
      return { code, label, rate };
    })
    .filter((c) => c.code)
    .slice(0, Math.max(0, num(max)));
}
// ── Excel-style grid column descriptor (GlassMaster spreadsheet look) ──
// PURE builder so the grid geometry / editable-set can be unit-tested. The
// live grid component derives left-offsets + sticky behaviour from this.
// `kind`: serial | text | item | dim | calcdim | int | fab | gst | calc | money | total | action.
// `bg`: ch (chargeable) | fab | pr (price) | cu (currency) tint groups.
//
// Options (driven by the top-section controls, mirroring the GlassMaster
// reference): { fabricationColumns, inputUnit (MM/IN/CM/FT — switches the
// dimension columns), areaVisible (SQFT/SQMT/RFT/RMT — filters the ACT/CHRG
// AREA sub-columns), showLocation (adds the pinned LOCATION column) }.
// A bare array is accepted as legacy `fabricationColumns`.
const AREA_ACT_KEY = { SQFT: 'actualSqft', SQMT: 'actualSqm', RFT: 'actualRft', RMT: 'actualRmt' };
const AREA_CHG_KEY = { SQFT: 'chargeSqft', SQMT: 'chargeSqm', RFT: 'chargeRft', RMT: 'chargeRmt' };
const AREA_PRECISION = { SQFT: 3, SQMT: 3, RFT: 3, RMT: 3 };
const AREA_TAG_ORDER = ['SQFT', 'SQMT', 'RFT', 'RMT'];

// Force the SQFT/SQMT area tag to match the document Price UOM (SQM ⇒ SQMT,
// SQF / blank ⇒ SQFT); RFT/RMT (perimeter) are preserved. Single strict source
// of truth for the document's area unit — mirrored by buildProformaColumns and
// the proforma controls panel so the whole document works in one UOM.
export function enforceAreaUom(areaVisible, priceUomCode) {
  const u = String(priceUomCode || '').toUpperCase();
  const uomTag = (u === 'SQM' || u === 'SQMT') ? 'SQMT' : 'SQFT';
  const rest = (Array.isArray(areaVisible) ? areaVisible : []).filter((t) => t === 'RFT' || t === 'RMT');
  return [uomTag, ...rest];
}

export function buildProformaColumns(options) {
  const opts = Array.isArray(options) ? { fabricationColumns: options } : (options || {});
  const fab = Array.isArray(opts.fabricationColumns) ? opts.fabricationColumns : [];
  const unit = ['MM', 'IN', 'CM', 'FT'].includes(opts.inputUnit) ? opts.inputUnit : 'MM';
  // Honour an EXPLICIT empty array (all area checkboxes off → no area
  // columns). Only fall back to SQFT when areaVisible was not supplied
  // at all (undefined / legacy bare-array call shape).
  const areaVisible = Array.isArray(opts.areaVisible) ? opts.areaVisible : ['SQFT'];
  const showLocation = opts.showLocation === true;
  // The PRICE columns are dynamic: `salesRate` always holds the rate in the
  // DOCUMENT's Price UOM (that is how calculateBasicAmount interprets it), and
  // `alternateRate` is the other area UOM. The headers MUST track the active
  // Price UOM — otherwise an SQM-priced rate renders under an "SQFT" label.
  // Stage 6CM.3 — the PRICE column header tracks the document's selling UOM.
  // Area UOMs (SQF/SQM) keep the SQFT/SQMT pair + alternate column; non-area
  // UOMs (PCS/RFT/RMT/MTR) show a single rate column labelled by the UOM.
  const rawPriceUom = String(opts.priceUomCode || '').toUpperCase();
  const isAreaPriceUomCol = rawPriceUom === '' || rawPriceUom === 'SQF' || rawPriceUom === 'SQFT'
    || rawPriceUom === 'SQM' || rawPriceUom === 'SQMT';
  const docPriceUom = (rawPriceUom === 'SQM' || rawPriceUom === 'SQMT') ? 'SQM' : 'SQF';
  const NON_AREA_PRICE_HEADER = { RFT: 'RFT', RMT: 'RMT', MTR: 'MTR', PCS: 'PCS' };
  const primaryPriceHeader = isAreaPriceUomCol
    ? (docPriceUom === 'SQM' ? 'SQMT' : 'SQFT')
    : (NON_AREA_PRICE_HEADER[rawPriceUom] || rawPriceUom);
  const altPriceHeader     = docPriceUom === 'SQM' ? 'SQFT' : 'SQMT';
  // STRICT Price-UOM rule (Stage 6CM.4): the PRICED area unit is LOCKED to the
  // document Price UOM — SQM ⇒ SQMT, SQF ⇒ SQFT — and always drives rate/basic.
  // The OPPOSITE area unit is a DISPLAY-ONLY reference column: it renders only
  // when explicitly toggled on (signalled by BOTH area tags present in
  // areaVisible), never affecting pricing. RFT/RMT (perimeter / edge-process
  // running length) stay independently toggleable. Non-area price UOMs
  // (PCS/RFT/RMT/MTR) keep the raw areaVisible.
  let tags = AREA_TAG_ORDER.filter((t) => areaVisible.includes(t));
  if (isAreaPriceUomCol) {
    // The document Price UOM's own area tag (SQM ⇒ SQMT, SQF ⇒ SQFT) always
    // shows when any area was requested; the secondary area is added only when
    // toggled on alongside it. RFT/RMT are kept as requested. An explicit empty
    // areaVisible still yields no area columns.
    const uomArea = docPriceUom === 'SQM' ? 'SQMT' : 'SQFT';
    const secondaryArea = uomArea === 'SQMT' ? 'SQFT' : 'SQMT';
    const hasAnyArea = tags.includes('SQFT') || tags.includes('SQMT');
    const keep = new Set(tags.filter((t) => t === 'RFT' || t === 'RMT'));
    if (hasAnyArea) keep.add(uomArea);
    if (tags.includes(uomArea) && tags.includes(secondaryArea)) keep.add(secondaryArea);
    tags = AREA_TAG_ORDER.filter((t) => keep.has(t));
  }

  const cols = [{ key: 'rowNum', group: '#', header: '#', width: 42, kind: 'serial', fixed: 'left' }];
  if (showLocation) cols.push({ key: 'location', field: 'location', group: 'LOCATION', header: 'LOCATION', width: 96, kind: 'text', fixed: 'left', editable: true });
  cols.push({ key: 'type', field: 'type', group: 'TYPE', header: 'TYPE', width: 81, kind: 'glasstype', fixed: 'left', editable: true });
  cols.push({ key: 'itemDesc', field: 'itemDesc', group: 'ITEM DESC', header: 'ITEM DESC', width: 240, kind: 'item', fixed: 'left', editable: true });

  const actG = `ACT (${unit})`; const chgG = `CHRG (${unit})`;
  // Inches need a touch more room (fraction strings like "23 5/16"): widen the
  // W/H dimension columns by 2% when the input unit is IN. The ACT/CHRG dimension
  // columns are 10% wider than their prior base for easier reading/editing.
  const actDimW = Math.round((unit === 'IN' ? 64 * 1.02 : 64) * 1.1);
  const chgDimW = Math.round((unit === 'IN' ? 62 * 1.02 : 62) * 1.1);
  cols.push({ key: 'actW', mmField: 'actualWidthMm', unit, group: actG, header: 'W', width: actDimW, kind: 'dim', editable: true });
  cols.push({ key: 'actH', mmField: 'actualHeightMm', unit, group: actG, header: 'H', width: actDimW, kind: 'dim', editable: true });
  // CHRG (MM): editability now follows the Company Defaults chargeable mode
  // (Stage 6CM.2). MANUAL_EDIT_ALLOWED (or AUTO + editChargeable) unlocks the
  // manual billing size; LOCKED_TO_ACTUAL / LOCKED_TO_WASTAGE keep it read-only.
  // Absent mode → AUTO, so legacy editChargeable behaviour is preserved.
  if (isChargeEditable(opts.chargeableMode, opts.editChargeable === true)) {
    cols.push({ key: 'chgW', mmField: 'manualChargeWidthMm', unit, group: chgG, header: 'W', width: chgDimW, kind: 'dim', bg: 'ch', editable: true });
    cols.push({ key: 'chgH', mmField: 'manualChargeHeightMm', unit, group: chgG, header: 'H', width: chgDimW, kind: 'dim', bg: 'ch', editable: true });
  } else {
    cols.push({ key: 'chgW', viewKey: 'chargeWidthMm', unit, group: chgG, header: 'W', width: chgDimW, kind: 'calcdim', bg: 'ch' });
    cols.push({ key: 'chgH', viewKey: 'chargeHeightMm', unit, group: chgG, header: 'H', width: chgDimW, kind: 'calcdim', bg: 'ch' });
  }
  cols.push({ key: 'qty', field: 'qty', group: 'QTY', header: 'QTY', width: 56, kind: 'int', editable: true });
  // Glass thickness (decimal, no unit suffix) + derived weight (kg). Thickness is
  // editable per pane; weight = thickness × 2.5 × actual m² (LAMI/DGU sum panes).
  cols.push({ key: 'thickness', field: 'thicknessMm', group: 'THICKNESS', header: 'THICKNESS', width: 80, kind: 'num', precision: 2, forceDecimals: true, editable: true });
  cols.push({ key: 'weight', viewKey: 'weight', group: 'WEIGHT', header: 'KG', width: 80, kind: 'calc', precision: 2 });

  tags.forEach((t) => cols.push({ key: `act_${t}`, viewKey: AREA_ACT_KEY[t], group: 'ACT AREA', header: t, width: 72, kind: 'calc', precision: AREA_PRECISION[t] }));
  tags.forEach((t) => cols.push({ key: `chg_${t}`, viewKey: AREA_CHG_KEY[t], group: 'CHRG AREA', header: t, width: 72, kind: 'calc', precision: AREA_PRECISION[t], bg: 'ch' }));

  fab.forEach((f) => cols.push({ key: `fab_${f.code}`, fabCode: f.code, fabRate: num(f.rate), group: 'FABRICATION', header: f.label || f.code, width: 50, kind: 'fab', bg: 'fab', editable: true }));

  // Visibility key: the SQFT/SQMT toggles (PROFORMA_TOGGLEABLE_COLUMNS) hide a
  // column by the UOM it SHOWS, not by which physical field carries it. The
  // SQFT-headed price column is keyed 'salesRate' for the toggle, the SQMT-headed
  // one 'alternateRate' — so "Price — SQFT rate" / "Price — SQMT rate" always hide
  // the right column regardless of the document Price UOM (salesRate is the SQMT
  // field when Price UOM = SQM). Non-area UOMs keep the physical key unchanged.
  const visForPriceHeader = (h) => (h === 'SQMT' ? 'alternateRate' : (h === 'SQFT' ? 'salesRate' : null));
  cols.push({ key: 'salesRate', field: 'salesRate', group: 'PRICE', header: primaryPriceHeader, width: 78, kind: 'num', precision: 2, bg: 'pr', editable: true, visKey: visForPriceHeader(primaryPriceHeader) || 'salesRate' });
  // Stage 6CM.3 — the alternate (other-area) rate column only applies to the
  // SQF/SQM pair; non-area UOMs (PCS/RFT/RMT/MTR) have a single rate.
  if (isAreaPriceUomCol) cols.push({ key: 'alternateRate', field: 'alternateRate', group: 'PRICE', header: altPriceHeader, width: 78, kind: 'num', precision: 2, bg: 'pr', editable: true, visKey: visForPriceHeader(altPriceHeader) || 'alternateRate' });
  cols.push({ key: 'basicAmount', group: 'BASIC', header: 'INR', width: 92, kind: 'money', bg: 'cu' });
  cols.push({ key: 'valueAddedAmount', group: 'VALUE ADD', header: 'INR', width: 90, kind: 'money', bg: 'cu' });
  cols.push({ key: 'discPct', field: 'discPct', group: 'DISCOUNT', header: '%', width: 54, kind: 'num', precision: 2, bg: 'cu', editable: true });
  cols.push({ key: 'lineDiscount', field: 'lineDiscount', group: 'DISCOUNT', header: 'AMT', width: 82, kind: 'num', precision: 2, bg: 'cu', editable: true });
  cols.push({ key: 'otherChargeAmount', field: 'otherChargeAmount', group: 'OTHER CHG', header: 'OTHER CHG', width: 90, kind: 'money', bg: 'cu' });
  cols.push({ key: 'gstRate', field: 'gstRate', group: 'GST', header: '%', width: 66, kind: 'gst', editable: true });
  cols.push({ key: 'gstAmount', group: 'GST', header: 'AMT', width: 84, kind: 'money' });
  // TOTAL column intentionally hidden from the proforma grid (2026-08-06 request).
  // Display-only: the document grand total (bottom panel / summary) and the
  // print/report compute line + document totals independently, so hiding this
  // column changes nothing financial. Restore by re-adding the push below.
  // cols.push({ key: 'lineTotal', group: 'TOTAL', header: 'TOTAL', width: 100, kind: 'total' });
  cols.push({ key: 'remarks1', field: 'remarks1', group: 'REMARKS', header: 'REMARK 1', width: 140, kind: 'text', editable: true });
  cols.push({ key: 'remarks2', field: 'remarks2', group: 'REMARKS', header: 'REMARK 2', width: 140, kind: 'text', editable: true });
  cols.push({ key: 'remarks3', field: 'remarks3', group: 'REMARKS', header: 'REMARK 3', width: 140, kind: 'text', editable: true });
  cols.push({ key: 'remarks4', field: 'remarks4', group: 'REMARKS', header: 'REMARK 4', width: 140, kind: 'text', editable: true });
  cols.push({ key: 'remarks5', field: 'remarks5', group: 'REMARKS', header: 'REMARK 5', width: 140, kind: 'text', editable: true });
  cols.push({ key: 'select', group: '', header: '', width: 38, kind: 'select', fixed: 'right' });

  // Tenant-wide column visibility (Proforma Grid Columns settings page).
  // `hiddenColumns` is a list of INDIVIDUAL column keys to drop. Essential
  // columns (#, TYPE, ITEM DESC, QTY, TOTAL, select) are never hidden.
  // Area / Location / Fabrication stay governed by the editor's controls
  // panel, so they are intentionally not toggleable here.
  const hidden = new Set(Array.isArray(opts.hiddenColumns) ? opts.hiddenColumns : []);
  if (hidden.size === 0) return cols;
  // Filter by the column's visibility key (visKey) when present — the price
  // columns map their toggle to the UOM shown (SQFT/SQMT), not the physical field.
  return cols.filter((c) => PROFORMA_ESSENTIAL_COLS.has(c.key) || !hidden.has(c.visKey || c.key));
}

// Columns that can never be hidden (kept regardless of the saved config).
// (lineTotal removed 2026-08-06 — the TOTAL column is no longer rendered.)
const PROFORMA_ESSENTIAL_COLS = new Set(['rowNum', 'type', 'itemDesc', 'qty', 'select']);

// Individual, per-column toggle list for the settings page. The `key`
// values MUST match the column keys produced above and the backend
// TOGGLEABLE_COLUMNS list. Area / Location / Fabrication are excluded
// (governed by the editor controls panel); essentials are excluded too.
export const PROFORMA_TOGGLEABLE_COLUMNS = Object.freeze([
  { key: 'actW',             label: 'Actual W' },
  { key: 'actH',             label: 'Actual H' },
  { key: 'thickness',        label: 'Thickness' },
  { key: 'weight',           label: 'Weight (kg)' },
  { key: 'chgW',             label: 'Charge W' },
  { key: 'chgH',             label: 'Charge H' },
  { key: 'salesRate',        label: 'Price — SQFT rate' },
  { key: 'alternateRate',    label: 'Price — SQMT rate' },
  { key: 'basicAmount',      label: 'Basic amount' },
  { key: 'valueAddedAmount', label: 'Value Add' },
  { key: 'discPct',          label: 'Discount %' },
  { key: 'lineDiscount',     label: 'Discount amount' },
  { key: 'otherChargeAmount', label: 'Other Charge' },
  { key: 'gstRate',          label: 'GST %' },
  { key: 'gstAmount',        label: 'GST amount' },
  { key: 'remarks1',         label: 'Remark 1' },
  { key: 'remarks2',         label: 'Remark 2' },
  { key: 'remarks3',         label: 'Remark 3' },
  { key: 'remarks4',         label: 'Remark 4' },
  { key: 'remarks5',         label: 'Remark 5' }
]);

// Cumulative left offset + total width for the absolute-positioned grid.
// When the user turns ON "edit chargeable", seed each line's manual chargeable
// W/H from the current auto (actual+wastage) value so the unlocked cells start
// at the present billing size. Existing manual values are kept (never clobbered).
export function prefillManualChargeDims(lines, header) {
  const h = header || {};
  return (Array.isArray(lines) ? lines : []).map((l) => {
    const aw = num(l.actualWidthMm); const ah = num(l.actualHeightMm);
    const cw = aw > 0 ? calculateChargeDimension(aw, h.wastageMode, h.wastageWidthMm) : 0;
    const ch = ah > 0 ? calculateChargeDimension(ah, h.wastageMode, h.wastageHeightMm) : 0;
    return {
      ...l,
      manualChargeWidthMm: num(l.manualChargeWidthMm) > 0 ? l.manualChargeWidthMm : cw,
      manualChargeHeightMm: num(l.manualChargeHeightMm) > 0 ? l.manualChargeHeightMm : ch
    };
  });
}

export function computeColumnLayout(columns) {
  const cols = Array.isArray(columns) ? columns : [];
  const colLeft = {};
  let left = 0;
  for (const c of cols) { colLeft[c.key] = left; left += num(c.width); }
  // Right offset for right-fixed columns (so multiple sticky-right columns —
  // e.g. the select checkbox + the delete action — stack instead of overlap).
  const colRight = {};
  let right = 0;
  for (let i = cols.length - 1; i >= 0; i -= 1) {
    const c = cols[i];
    if (c.fixed === 'right') { colRight[c.key] = right; right += num(c.width); }
  }
  const fixedLeft = cols.filter((c) => c.fixed === 'left');
  return {
    colLeft, colRight, totalWidth: left,
    lastFixedLeftKey: fixedLeft.length ? fixedLeft[fixedLeft.length - 1].key : null
  };
}

// ════════════════════════════════════════════════════════════════════
// Stage 6Q.2 — Smart Item-Description (#process@rate/uom) + value-add
// ════════════════════════════════════════════════════════════════════
// GlassMaster lets the item description carry edge-process value-add inline:
//   "12MM CLEAR TOUGHENED GLASS WITH CEP #Flat Polish@90/RFT #Frosting@100/SQF"
// The part before the first '#' is the BASE product; each '#name@rate/uom'
// is a value-add process. The display string is composed as
//   "<BASE> WITH A, B AND C" while the raw (with #tags) is kept for editing.
// Value-add amount = Σ rate × area-by-uom × qty. PURE — unit-testable.

export const PROCESS_UOMS = ['RFT', 'RMT', 'SQF', 'SQFT', 'SQM', 'SQMT', 'PCS'];

// Parse a raw item description into base + tags + pricing (+ soft errors).
// Extract the glass thickness (in mm) from an item description. Glass items
// embed thickness as an "NMM" / "N.NMM" token, e.g. "4MM SGG CLEAR ANNEALED
// GLASS" → 4, "12.76MM LAMINATED" → 12.76. Returns the FIRST such token (the
// glass thickness) or null when absent. Drives thickness-wise process pricing
// in the "#process" tag picker.
export function parseThicknessMm(desc) {
  const m = /(\d+(?:\.\d+)?)\s*MM\b/i.exec(String(desc == null ? '' : desc));
  return m ? num(m[1]) : null;
}

// Resolve a process's rate for a given glass thickness. Standard business rule:
// use the thickness-specific rate when one is defined for that thickness, else
// fall back to the process default rate. `process` is a lookup row carrying
// { defaultRate, thicknessRates: [{ thicknessMm, rate }] }. Mirrors the
// backend resolveProcessRate (a thickness row that EXISTS wins, even if 0).
export function resolveProcessRateForThickness(process, thicknessMm) {
  const p = process || {};
  const def = num(p.defaultRate != null ? p.defaultRate : (p.rate != null ? p.rate : p.ratePerUnit));
  const t = thicknessMm == null ? null : num(thicknessMm);
  if (t != null && t > 0 && Array.isArray(p.thicknessRates)) {
    const hit = p.thicknessRates.find((tr) => tr && Math.abs(num(tr.thicknessMm) - t) < 0.001);
    if (hit) { const rr = num(hit.rate); return rr > 0 ? round2(rr) : 0; }
  }
  return def > 0 ? round2(def) : 0;
}

export function parseItemDesc(raw) {
  const text = (raw == null ? '' : String(raw));
  const parts = text.split('#');
  const base = (parts[0] || '').trim();
  const tags = [];
  const pricing = [];
  const errors = [];
  const seen = Object.create(null);
  for (let i = 1; i < parts.length; i += 1) {
    const seg = parts[i].trim();
    if (!seg) continue;
    const atIdx = seg.indexOf('@');
    if (atIdx < 0) {
      tags.push(seg);
      pricing.push({ name: seg, rate: 0, uom: '' });
      errors.push({ tag: seg, msg: `Missing @rate/uom. Use: #${seg}@RATE/UOM` });
    } else {
      const name = seg.slice(0, atIdx).trim();
      const after = seg.slice(atIdx + 1).trim();
      const slash = after.indexOf('/');
      const ratePart = slash < 0 ? after : after.slice(0, slash).trim();
      const uomPart = slash < 0 ? '' : after.slice(slash + 1).trim().toUpperCase();
      const rate = num(ratePart);
      if (!name) { errors.push({ tag: seg, msg: 'Process name is empty before @' }); continue; }
      tags.push(name);
      pricing.push({ name, rate: rate > 0 ? round2(rate) : 0, uom: uomPart });
      if (!(rate > 0)) errors.push({ tag: name, msg: `Rate is empty/zero. Use: #${name}@RATE/UOM` });
      else if (!uomPart) errors.push({ tag: name, msg: `Missing /UOM. Use: #${name}@${ratePart}/UOM` });
      else if (!PROCESS_UOMS.includes(uomPart)) errors.push({ tag: name, msg: `Invalid UOM "${uomPart}". Valid: ${PROCESS_UOMS.join(', ')}` });
    }
    const key = (tags[tags.length - 1] || '').toUpperCase();
    if (key) { if (seen[key]) errors.push({ tag: tags[tags.length - 1], msg: `Duplicate process "${tags[tags.length - 1]}"` }); seen[key] = true; }
  }
  return { base, tags, pricing, errors };
}

// Compose the human display string from a base + tag list.
export function composeItemDesc(base, tags) {
  const b = (base == null ? '' : String(base)).trim();
  const list = (Array.isArray(tags) ? tags : []).map((t) => String(t).trim().toUpperCase()).filter(Boolean);
  if (!b) return '';
  if (list.length === 0) return b.toUpperCase();
  const suffix = list.length === 1
    ? `WITH ${list[0]}`
    : `WITH ${list.slice(0, -1).join(', ')} AND ${list[list.length - 1]}`;
  return `${b.toUpperCase()} ${suffix}`;
}

// Rebuild the raw (#tag) string from base + tags — used to seed the editor.
export function rawFromBaseTags(base, tags) {
  const list = Array.isArray(tags) ? tags : [];
  if (list.length === 0) return (base == null ? '' : String(base));
  return `${base} ${list.map((t) => `#${t}`).join(' ')}`;
}

// Value-add amount from parsed process pricing × area-by-uom × qty.
// ── Edge (per-side) running length — mirrors the backend ─────────────
// L1 = top (WIDTH) · L2 = right (HEIGHT) · L3 = bottom (WIDTH) · L4 = left
// (HEIGHT). Billable length = Σ selected-edge lengths ÷ ft (RFT) / m (RMT).
// All four = full perimeter, so no selection reproduces today's math.
export function edgeRunningLength(sides, wMm, hMm, unit) {
  const w = num(wMm), h = num(hMm);
  if (w <= 0 || h <= 0) return 0;
  const sel = Array.isArray(sides) ? sides.map((s) => String(s || '').toUpperCase()) : [];
  if (!sel.length) return 0;
  let mm = 0;
  if (sel.includes('L1')) mm += w;
  if (sel.includes('L3')) mm += w;
  if (sel.includes('L2')) mm += h;
  if (sel.includes('L4')) mm += h;
  const denom = String(unit || 'RFT').toUpperCase() === 'RMT' ? MM_PER_M : MM_PER_FT;
  return round4(mm / denom);
}
// Stable per-process signature so an edge selection binds to the right #tag row.
export function processEdgeKey(p) {
  const name = String((p && p.name) || '').trim();
  const rate = num(p && p.rate);
  const uom  = String((p && p.uom) || '').toUpperCase();
  return `${name}|${rate}|${uom}`;
}

export function calculateValueAddedAmount(processPricing, areas) {
  const list = Array.isArray(processPricing) ? processPricing : [];
  const a = areas || {};
  const qty = Math.max(0, num(a.qty));
  if (qty <= 0) return 0;
  const edgeDetail = a.edgeDetail && typeof a.edgeDetail === 'object' ? a.edgeDetail : {};
  const basis = String(a.edgeworkBasis || 'CHARGE').toUpperCase();
  const wMm = basis === 'ACTUAL' ? num(a.actualWidthMm)  : num(a.chargeWidthMm);
  const hMm = basis === 'ACTUAL' ? num(a.actualHeightMm) : num(a.chargeHeightMm);
  let total = 0;
  for (const p of list) {
    const rate = num(p && p.rate);
    if (rate <= 0) continue;
    const uom = String((p && p.uom) || '').toUpperCase();
    let areaForUom;
    if (uom === 'SQM' || uom === 'SQMT') areaForUom = num(a.chargeSqm);
    else if (uom === 'RFT' || uom === 'RMT') {
      const detail = edgeDetail[processEdgeKey(p)];
      // Stored selection (entry present) honoured exactly, incl. empty = ₹0. No
      // entry → full perimeter (legacy parity). Mirrors the backend.
      if (detail && Array.isArray(detail.sides)) {
        areaForUom = edgeRunningLength(detail.sides, wMm, hMm, uom);
      } else {
        areaForUom = uom === 'RFT' ? num(a.chargeRft) : num(a.chargeRmt);
      }
    } else if (uom === 'PCS') areaForUom = 1;
    else areaForUom = num(a.chargeSqft); // SQF/SQFT/blank default
    if (areaForUom > 0) total += rate * areaForUom * qty;
  }
  return round2(total);
}

// ── Value-added (process) charge breakdown ──────────────────────────
// Itemises the #tag@rate/uom processes that calculateValueAddedAmount folds into
// each line's valueAddedAmount, for the proforma "Value Added Services" table
// (PROCESS · RATE · UOM · AREA · AMOUNT). Operates on COMPUTED tree rows whose
// chargeSqft/chargeSqm/chargeRft/chargeRmt are already ×qty totals, and honours
// the SAME isBilled predicate as computeProformaPreview (skip roll-up parents,
// SHEET/GASFILL no-money types, and combo BOM children) so the breakdown matches
// totals.valueAddedAmount up to per-line rounding. Rows are grouped by
// (name, rate, uom) — two FLAT POLISH rates stay two rows, as in the reference.
//
// MUST stay in lockstep with calculateValueAddedAmount's uom→area mapping; the
// Σ-vs-total invariant is asserted in proformaHelpers.test.js.
export function breakdownValueAddedCharges(rows, edgeworkBasis) {
  const list = Array.isArray(rows) ? rows : [];
  const basis = String(edgeworkBasis || 'CHARGE').toUpperCase();
  const parentSet = new Set(list.filter((l) => l && l.parentRefUuid).map((l) => l.parentRefUuid));
  const isBilled = (l) => {
    if (!l) return false;
    // WO-3.3 — a Change-Order struck original / waived (non-chargeable) cancelled
    // cut-piece line bills no value-add (its valueAddedAmount is 0 in computeLine
    // and it is excluded from the taxable roll-up by isBilledLeaf). Mirror that
    // here so the Value-Added breakdown matches the billed total. Inert for LIVE.
    const cs = String(l.lineState || '').toUpperCase();
    if (cs === 'STRUCK' || (cs === 'CANCELLED' && l.chargeable === false)) return false;
    if (l.comboNoBill === true) return false;
    if (l.comboBilled === true) return true;
    return !parentSet.has(l.refUuid) && !NO_MONEY_TYPES.includes(String(l.type || '').toUpperCase());
  };
  // Total charge area for a process uom (row areas already include qty; PCS bills
  // per piece so its "area" is the line qty — mirrors calculateValueAddedAmount).
  // For RFT/RMT, a per-side edge selection on the line bills only the chosen
  // edges (its running length already reflects W×H, i.e. a per-piece length, so
  // it is scaled by qty to match the ×qty row areas); no selection = perimeter.
  const areaForUom = (uom, l, p) => {
    const u = String(uom || '').toUpperCase();
    if (u === 'SQM' || u === 'SQMT') return num(l.chargeSqm);
    if (u === 'RFT' || u === 'RMT') {
      const ed = l.edgeDetail && typeof l.edgeDetail === 'object' ? l.edgeDetail : {};
      const detail = ed[processEdgeKey(p)];
      // Stored selection (entry present) honoured exactly, incl. empty = 0. No
      // entry → full perimeter. Mirrors calculateValueAddedAmount.
      if (detail && Array.isArray(detail.sides)) {
        const wMm = basis === 'ACTUAL' ? num(l.actualWidthMm) : num(l.chargeWidthMm);
        const hMm = basis === 'ACTUAL' ? num(l.actualHeightMm) : num(l.chargeHeightMm);
        return edgeRunningLength(detail.sides, wMm, hMm, u) * Math.max(0, num(l.qty));
      }
      return u === 'RFT' ? num(l.chargeRft) : num(l.chargeRmt);
    }
    if (u === 'PCS') return num(l.qty);
    return num(l.chargeSqft); // SQF / SQFT / blank default
  };
  const map = new Map();
  for (const l of list) {
    if (!isBilled(l)) continue;
    const pricing = Array.isArray(l.processPricing) ? l.processPricing : [];
    for (const p of pricing) {
      const rate = num(p && p.rate);
      if (rate <= 0) continue;
      const uom = String((p && p.uom) || '').toUpperCase();
      const area = areaForUom(uom, l, p);
      if (area <= 0) continue;
      const name = String((p && p.name) || '').trim() || '—';
      const key = `${name}|${rate}|${uom}`;
      const cur = map.get(key) || { key, name, rate, uom, area: 0, amount: 0 };
      cur.area += area;
      cur.amount += rate * area;
      map.set(key, cur);
    }
  }
  const out = Array.from(map.values()).map((r) => ({
    key: r.key, name: r.name, rate: r.rate, uom: r.uom,
    area: round4(r.area), amount: round2(r.amount)
  }));
  const total = round2(out.reduce((s, r) => s + num(r.amount), 0));
  return { rows: out, total };
}

// ── Inch fraction display / parse (GlassMaster nearest-1/16 inch) ──
export function decimalToFraction16(val) {
  const v = num(val);
  if (val == null || !Number.isFinite(Number(val))) return '';
  const neg = v < 0;
  let whole = Math.floor(Math.abs(v));
  let rem = Math.abs(v) - whole;
  let sixteenths = Math.round(rem * 16);
  if (sixteenths === 16) { whole += 1; sixteenths = 0; }
  if (sixteenths === 0) return (neg ? '-' : '') + String(whole);
  let n = sixteenths; let d = 16;
  while (n % 2 === 0) { n /= 2; d /= 2; }
  if (whole === 0) return `${neg ? '-' : ''}${n}/${d}`;
  return `${neg ? '-' : ''}${whole} ${n}/${d}`;
}
export function parseFractionInput(str) {
  if (str == null) return null;
  const s = String(str).trim();
  if (s === '') return null;
  if (/^-?\d+\.?\d*$/.test(s)) return Number(s);
  let m = s.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) { const w = parseInt(m[1], 10); const a = parseInt(m[2], 10); const b = parseInt(m[3], 10); if (b === 0) return null; const sign = w < 0 ? -1 : 1; return sign * (Math.abs(w) + a / b); }
  m = s.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (m) { const a = parseInt(m[1], 10); const b = parseInt(m[2], 10); if (b === 0) return null; return a / b; }
  return null;
}
export function isValidInchInput(str) {
  if (str == null || String(str).trim() === '') return true;
  const s = String(str).trim();
  return /^\d+$/.test(s) || /^\d+\.\d*$/.test(s) || /^\d+\/\d+$/.test(s) || /^\d+ \d+\/\d+$/.test(s);
}
export function filterInchChar(str) {
  return String(str == null ? '' : str).replace(/[^0-9 /.]/g, '').replace(/ {2,}/g, ' ');
}

export function getFabCount(line, code) {
  const entries = line && line.fabricationDetails && Array.isArray(line.fabricationDetails.entries)
    ? line.fabricationDetails.entries : [];
  const e = entries.find((x) => x && x.code === code);
  return e ? num(e.count) : 0;
}
export function setFabCount(line, code, rate, count) {
  const fd = (line && line.fabricationDetails) || {};
  const entries = Array.isArray(fd.entries) ? fd.entries.slice() : [];
  const idx = entries.findIndex((x) => x && x.code === code);
  const c = num(count);
  if (c <= 0) {
    if (idx >= 0) entries.splice(idx, 1);
  } else if (idx >= 0) {
    entries[idx] = { ...entries[idx], code, rate: num(rate), count: c };
  } else {
    entries.push({ code, rate: num(rate), count: c });
  }
  return { ...fd, entries };
}
